import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { App as AntApp, Input, Button, Modal, Switch } from 'antd';
import SheetDate from '../components/SheetDate';
import SheetSelect, { OptionSheet } from '../components/SheetSelect';
import AppBar from '../components/AppBar';
import FileViewerLazy from '../components/FileViewerLazy';
import FormSheet from '../components/FormSheet';
import SaleNotesSheet from '../components/notes/SaleNotesSheet';
import { AsyncButton, Chip, Skeletons, RM, MoneyInput, initials, markPicked } from '../components/kit';
import usePagedOptions from '../hooks/usePagedOptions';
import { commissionColor, commissionTypeLabel } from '../lib/commission';
import { partyWords } from '../lib/partyWords';
import { rowShareLabel } from '../lib/coAgencyShare';
import { splitLinkedForms, agreementLabel } from '../lib/saleLinkedForms';
import { renLabel } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import * as salesApi from '../api/sales';
import * as contactsApi from '../api/contacts';
import * as teamApi from '../api/team';
import * as coShareFormsApi from '../api/coShareForms';
import AgentBranch from '../components/AgentBranch';

// Party / agent picker page size — the sheets scroll the next page in.
const CONTACT_PAGE = 20;

// Internal co-share form status → the chip tone and the word the agent reads.
const CS_TONE = { DRAFT: 'brand', PENDING_SIGNING: 'warn', SIGNED: 'ok', SUPERSEDED: 'muted', VOID: 'bad' };
const CS_LABEL = { DRAFT: 'Draft', PENDING_SIGNING: 'Awaiting signatures', SIGNED: 'Signed', SUPERSEDED: 'Superseded', VOID: 'Void' };

// Receipt fee/payer options — mirror the admin SaleReceiptsTab.
const FEE_TYPE_OPTIONS = [
  { value: 'EARNEST_DEPOSIT', label: 'Earnest Deposit' },
  { value: 'SECURITY_DEPOSIT', label: 'Security Deposit' },
  { value: 'UTILITY_DEPOSIT', label: 'Utility Deposit' },
  { value: 'STAMPING_FEE', label: 'Stamping Fee' },
  { value: 'SST', label: 'SST' },
  { value: 'OTHER', label: 'Other' },
];
const PAYER_TYPE_OPTIONS = [
  { value: 'VENDOR', label: 'Vendor' }, { value: 'LANDLORD', label: 'Landlord' },
  { value: 'PURCHASER', label: 'Purchaser' }, { value: 'TENANT', label: 'Tenant' }, { value: 'DEVELOPER', label: 'Developer' },
  // On a co-broke the other agency often collects the deposit and banks it in to us.
  { value: 'CO_AGENCY', label: 'Co-Agency' },
];

// Balance To — the specific word a payee carries. One table serves both halves of a
// deal (a vendor row is a landlord on a rental), which is why the group is called
// "Landlord / Vendor" while the party itself is one or the other.
const BALANCE_ROLE_LABEL = { LANDLORD: 'Landlord', VENDOR: 'Vendor', TENANT: 'Tenant', PURCHASER: 'Purchaser', AGENT: 'Agent', LAWYER: 'Lawyer' };
// A group can legitimately hold nobody — say which party is missing and where it is
// added, rather than showing an empty picker the agent can only stare at.
// Only the contact-backed groups can be empty. A lawyer is typed in, so there is no
// party of theirs to be missing and Lawyer is absent here on purpose.
const BALANCE_EMPTY_HINT = {
  VENDOR: 'No landlord or vendor is on this sale yet — add one on the Parties tab.',
  PURCHASER: 'No tenant or purchaser is on this sale yet — add one on the Parties tab.',
  AGENT: 'You are not listed as an agent on this sale.',
};
// The two things this tab records, and they are not the same instruction: a
// BALANCE is what is left over being passed to whoever it belongs to, a REFUND is
// somebody's own money going back to them. HQ finance settles them differently, so
// the entry says which it is instead of leaving it to the remarks.
const BALANCE_ENTRY_TYPE_LABEL = { BALANCE_TO: 'Balance To', REFUND: 'Refund' };
const BALANCE_ENTRY_TYPE_TONE = { BALANCE_TO: 'brand', REFUND: 'orange' };
const BALANCE_ENTRY_TYPE_SUB = {
  BALANCE_TO: 'What’s left once the invoice is settled, passed to whoever it belongs to',
  REFUND: 'Money going back to whoever paid it in — a deposit on a deal that fell through, an over-collection',
};
// The same picker asks two questions. Naming it after the answer keeps "who does
// this belong to" and "who is being paid back" from reading as one question.
const BALANCE_PAYEE_LABEL = { BALANCE_TO: 'Balance to', REFUND: 'Refund to' };
const balanceNoun = (t) => (t === 'REFUND' ? 'refund' : 'balance');
// Sentinel row in the bank-account picker. Real account ids are uuids, so nothing collides.
const NEW_BANK = '__new__';

// Module order mirrors the admin sale workspace (Overview → Commissions → Attachments
// → Receipts → Invoices → finance views → Parties); Notes stays as the last tab.
// 'comments' left the tab row — notes open from the floating button instead (admin parity).
const SEGS = ['overview', 'commissions', 'balance', 'attachments', 'receipts', 'invoices', 'finance', 'parties'];
const SEG_LABEL = { overview: 'Overview', commissions: 'Commissions', balance: 'Balance / Refund', attachments: 'Attachments', receipts: 'Receipts', invoices: 'Invoices', finance: 'Finance', parties: 'Parties', comments: 'Notes' };

// Invoice / refund status → Chip tone (mirror the admin pill colours).
const INVOICE_TONE = { SETTLED: 'ok', PARTIAL: 'warn', VOIDED: 'bad' };
const REFUND_TONE = { ISSUED: 'ok', VOIDED: 'bad' };
// knockoff_application_status_enum — the official receipt raised against an invoice.
const PAYMENT_TONE = { COMPLETED: 'ok', PENDING_PUT: 'warn', VOIDED: 'bad' };

// Happy-path stage sequence (labels mirror the server STAGE_LABEL) for the Overview tracker.
// Per-stage colours — identical to the admin portal's stage chevron bar, so both
// apps read the same visual language. Keyed by the server's STAGE_LABEL.
const STAGE_COLOR = {
  Draft: '#8c8c8c',
  Balance: '#1890ff',
  'Request invoice': '#1890ff',
  'Request receipt': '#faad14',
  'Ready to claim': '#faad14',
  'Documents completed': '#13c2c2',
  'HQ verified': '#52c41a',
  'HQ approved': '#389e0d',
  Paid: '#237804',
  Cancelled: '#ff4d4f',
  Rejected: '#ff4d4f',
};

// Per-commission-type colours live in lib/commission.js — the Earnings commission
// sheet draws the same lines and has to colour them identically.

// Stages an agent may set — their own leg of the chevron, up to Ready To Claim.
// Mirrors AGENT_SETTABLE_STAGES on the server; the server is the authority and
// the sheet only ever offers what `settable_stages` actually contains.
//
// Everything past Ready To Claim records HQ's own checks, not the agent's
// progress, so those stay HQ's.
//
// The KEYS of AGENT_STAGE_LABEL are enum values; the VALUES are looked up in
// STAGE_COLOR, which is keyed by the server's stageLabel string. Change a label
// here without changing it there and the row silently falls back to the brand
// colour while the rail badge keeps the real one.
const AGENT_STAGE_LABEL = {
  DRAFT: 'Draft',
  BALANCE_TO: 'Balance to owner',
  REQUEST_INVOICE: 'Request invoice',
  REQUEST_RECEIPT: 'Request receipt',
  READY_TO_CLAIM: 'Ready to claim',
  CANCEL_REFUND_LOAN_REJECT: 'Cancelled',
};
// The agent's own run of the chevron, in the order it is walked. This array is
// the render list — a stage the server allows but this array omits renders
// nothing at all, with no error.
const AGENT_REQUEST_STAGES = ['DRAFT', 'BALANCE_TO', 'REQUEST_INVOICE', 'REQUEST_RECEIPT', 'READY_TO_CLAIM'];
// The deal collapsed in the field. Same mechanism, different meaning — kept apart in
// the sheet and confirmed with a danger action.
const AGENT_EXCEPTION_STAGE = 'CANCEL_REFUND_LOAN_REJECT';
const STAGE_BLURB = {
  DRAFT: 'Still putting the deal together.',
  BALANCE_TO: 'Balance worked out and passed to the owner.',
  REQUEST_INVOICE: 'Ask HQ to raise the invoice for this deal.',
  REQUEST_RECEIPT: 'Payment received — ask HQ to issue the official receipt to the customer.',
  READY_TO_CLAIM: 'Tell HQ the paperwork is in and you\'re claiming.',
  CANCEL_REFUND_LOAN_REJECT: 'Loan rejected, buyer withdrew, or the deal is off.',
};

// sale_attachment_category_enum — the 14 document types, in the SAME order as the
// admin portal's SaleAttachmentsTab dropdown.
// `group` sorts these into the picker; the ARRAY ORDER is still the admin portal's
// dropdown order and nothing else depends on the grouping.
const CATEGORY_OPTIONS = [
  { value: 'CO_AGENCY_LETTER', label: 'Co-Agency Letter', group: 'AUTH' },
  { value: 'GENERAL_AUTHORIZATION_FORM', label: 'General Authorization Form', group: 'AUTH' },
  { value: 'ATP_ATR', label: 'ATP / ATR', group: 'AUTH' },
  { value: 'CO_AGENCY_INVOICE', label: 'Co-Agency Invoice', group: 'BILLING' },
  { value: 'BANK_IN_SLIP', label: 'Bank-In Slip', group: 'BILLING' },
  { value: 'SPA_TA', label: 'SPA / TA', group: 'DEAL' },
  { value: 'INVOICE', label: 'Invoice', group: 'BILLING' },
  { value: 'REFERRAL_IC_COPY_INVOICE', label: 'Referral / IC Copy / Invoice', group: 'BILLING' },
  { value: 'CANCELLATION_FORFEITED', label: 'Cancellation / Forfeited', group: 'OFF' },
  { value: 'UNDERTAKING_LETTER', label: 'Undertaking Letter', group: 'AUTH' },
  { value: 'LOAN_REJECT', label: 'Loan Rejection Letter', group: 'OFF' },
  { value: 'IC_COPY_PARTIES', label: 'IC Copy (Both Parties)', group: 'DEAL' },
  { value: 'SSM_FORM24_49', label: 'SSM / Form 24 / 49', group: 'DEAL' },
  { value: 'OTHER_DOCUMENTS', label: 'Other Documents', group: 'OTHER' },
];

// Fourteen flat labels is a wall; four headings you already think in is a menu.
// The groups follow the life of a deal — the one structure every agent carries in
// their head — rather than the enum's order, which is the admin dropdown's order,
// which is nobody's mental model.
const CATEGORY_GROUPS = [
  { key: 'AUTH', label: 'Authorisation' },
  { key: 'DEAL', label: 'The deal' },
  { key: 'BILLING', label: 'Billing & payment' },
  { key: 'OFF', label: 'If it fell through' },
  { key: 'OTHER', label: 'Other' },
];

// Advisory (non-blocking) checklist of the core claim documents — mirrors the
// admin portal's REQUIRED_DOCS soft warning. Each requirement is satisfied when
// ANY of its categories is attached, so an either/or requirement (IC copy OR SSM —
// a party is either an individual or a company) is met by providing just one.
const REQUIRED_DOCS = [
  { categories: ['GENERAL_AUTHORIZATION_FORM'] },
  { categories: ['ATP_ATR'] },
  { categories: ['BANK_IN_SLIP'] },
  { categories: ['SPA_TA'] },
  { categories: ['IC_COPY_PARTIES', 'SSM_FORM24_49'], label: 'IC Copy / SSM' },
];
const docLabel = (req) => req.label || (CATEGORY_OPTIONS.find((o) => o.value === req.categories[0]) || {}).label || req.categories[0];

export default function SaleDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message, modal } = AntApp.useApp();
  const [notesOpen, setNotesOpen] = useState(false); // notes now live in a floating sheet (admin comments-drawer parity)
  const { agent: me } = useAuth();
  // Which module you are on lives in the URL, not in component state. `?tab=`
  // lands the reader on the tab whatever sent them here meant — a co-share that
  // has produced a sale points at Attachments, because the co-share form is the
  // next thing owed and that is where it is raised — and, because switching tabs
  // writes it back, a refresh or a back press out of a receipt/document/co-share
  // returns to the tab the agent was working in instead of resetting to Overview.
  // Unknown values fall back to Overview rather than rendering an empty segment.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedSeg = searchParams.get('tab');
  const seg = SEGS.includes(wantedSeg) ? wantedSeg : 'overview';
  // `replace` so the tab row doesn't stack history entries — back should leave
  // the sale, not walk the agent backwards through every tab they touched.
  // Overview is the default, so it stays out of the URL.
  // A tab switch only rewrites `?tab=`; every edit in progress lives on this page
  // and survives it, so the leave guard lets it through (partiesGuard, below).
  const setSeg = (s) => {
    partiesGuard.allowOnce();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (s === 'overview') next.delete('tab'); else next.set('tab', s);
      return next;
    }, { replace: true });
  };

  const [stageSheet, setStageSheet] = useState(false);

  // Confirm before sending: the agent must see exactly what changes (from → to).
  // Reporting a collapsed deal gets a heavier warning than asking for an invoice —
  // it's the same mechanism but a materially different thing to say to HQ.
  const confirmStageChange = (stage) => {
    if (!stage) return;
    setStageSheet(false);
    const exception = stage === 'CANCEL_REFUND_LOAN_REJECT';
    modal.confirm({
      title: exception ? 'Report this deal as cancelled?' : 'Change the sale stage?',
      content: (
        <span>
          {sale?.ref} will move from <b>“{sale?.stageLabel}”</b> to <b>“{AGENT_STAGE_LABEL[stage] || stage}”</b>.
          {exception
            ? ' HQ will review what happens to any money already collected.'
            : ' HQ will see this as your request signal.'}
        </span>
      ),
      okText: exception ? 'Yes, report it' : 'Yes, change stage',
      okButtonProps: exception ? { danger: true } : undefined,
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        await salesApi.updateSalesStageById(id, { stage });
        message.success(`Stage changed to “${AGENT_STAGE_LABEL[stage] || stage}”`);
        load();
      },
    });
  };
  const [sale, setSale] = useState(null);
  const [fin, setFin] = useState(null); // finance view — lazy-loaded when the Finance tab opens
  const [cb, setCb] = useState(null); // full commission breakdown — re-read whenever the Commissions tab opens
  const [cbErr, setCbErr] = useState(false); // that read failed outright (offline / server down)
  const [cbTick, setCbTick] = useState(0);   // bumped by Retry to force another read
  // FAB badge = how many human comments the agent hasn't read. ERP/audit entries
  // and their own notes never count, so a badge means someone actually said
  // something to them.
  const [noteUnread, setNoteUnread] = useState(0);
  const [atts, setAtts] = useState([]);
  /**
   * The attachments the list actually draws.
   *
   * The internal co-share form has its own row above, rendered from the form
   * record — status, shares, who has signed, and a way into the document. Its
   * `sale_attachments` row is the join that files it against the sale, not a file
   * anyone uploaded, so drawing it as well printed the same document twice: once
   * properly, and once as a bare ref with a Delete button that would have
   * unfiled a signed agreement.
   *
   * `atts` itself stays whole — the required-documents checklist reads it, and
   * being filed IS what satisfies the requirement.
   */
  const uploadedAtts = atts.filter((a) => !a.co_share_form_id);
  // Category chosen in the type-picker sheet, held in a ref so the file-input's
  // change handler always reads the value picked in this tap.
  const pendingCat = useRef('OTHER_DOCUMENTS');
  const [uploading, setUploading] = useState(false);
  const [fileView, setFileView] = useState(null); // { name, url } → in-app PDF/image viewer
  const fileRef = useRef(null);

  // { applicable, agent_count, form, history } — see api/coShareForms.js
  const [csf, setCsf] = useState(null);
  // Candidate letterheads, when the RENs sit in different branches and the server
  // needs someone to pick. Null unless it has asked; setting it opens the modal.
  const [branchAsk, setBranchAsk] = useState(null);
  const [branchPick, setBranchPick] = useState(null);
  const [catOpen, setCatOpen] = useState(false); // document-type picker sheet
  const [removeTarget, setRemoveTarget] = useState(null); // attachment pending remove confirm
  const [catQ, setCatQ] = useState(''); // its search box — browse first, type only if you want to
  const [rcOpen, setRcOpen] = useState(false);   // record-a-receipt sheet

  const emptyRc = () => ({ fee_type: 'EARNEST_DEPOSIT', fee_type_other: '', payer_type: undefined, payer_name: '', amount: '', receipt_date: dayjs(), notes: '', attachment_ids: [] });
  const [rc, setRc] = useState(emptyRc());
  // setRcv is only ever the agent's own edit, so it is what marks the sheet dirty.
  const [rcDirty, setRcDirty] = useState(false);
  const setRcv = (patch) => { setRcDirty(true); setRc((s) => ({ ...s, ...patch })); };
  const rcGuard = useUnsavedChanges(rcOpen && rcDirty, { what: 'the receipt' });
  // The sheet keeps its fields between openings, so throwing the typing away has
  // to clear them too — or "discard" would hand them straight back next time.
  const closeRc = () => rcGuard.confirmClose(() => { setRcOpen(false); setRc(emptyRc()); setRcDirty(false); });

  // ── Balance To ── who finance pays out of what is left in the branch Client
  // Account once the commission invoice is settled. Both reads are lazy — the tab
  // is the only thing that needs them — and `btPayees` carries this sale's parties
  // with their saved bank accounts, so the form asks the server nothing once open.
  const [bt, setBt] = useState(null);          // { entries, total, count }
  const [btPayees, setBtPayees] = useState(null);
  const [btOpen, setBtOpen] = useState(false); // add / edit sheet
  const [btEdit, setBtEdit] = useState(null);  // the entry being edited; null when adding
  // Opens on Balance To rather than on nothing: it is far and away the common
  // case, and an unanswered first question on a five-question sheet reads as work.
  const emptyBf = () => ({ entry_type: 'BALANCE_TO', party_type: undefined, party_id: undefined, party_name: '', amount: '', bank_account_id: undefined, bank_name: '', account_no: '', account_holder_name: '', remarks: '' });
  const [bf, setBf] = useState(emptyBf());
  // Same split as the receipt: openBalance seeds `bf` directly, setBfv is the agent.
  const [btDirty, setBtDirty] = useState(false);
  const setBfv = (patch) => { setBtDirty(true); setBf((s) => ({ ...s, ...patch })); };
  const btGuard = useUnsavedChanges(btOpen && btDirty, { what: btEdit ? 'this entry' : 'the new entry' });
  const closeBt = () => btGuard.confirmClose(() => { setBtOpen(false); setBtEdit(null); });

  const load = () => salesApi.getSalesById(id).then((r) => setSale(r?.data ?? r)).catch((e) => message.error(e.message));
  const loadAtts = () => salesApi.listSalesAttachments(id).then((r) => setAtts((r?.data ?? r) || [])).catch(() => {});
  // The internal co-share form for this sale: whether one is even applicable (a
  // one-agent sale has nothing to share), the live one if it exists, and whether
  // the split has moved since it was signed. Probed quietly — a sale must still
  // open if this endpoint is unavailable.
  const loadCoShareForm = () => coShareFormsApi.getSaleCoShareForm(id)
    .then((r) => setCsf(r?.data ?? r)).catch(() => setCsf(null));

  /**
   * Raise the co-share form for this sale.
   *
   * Called twice on the ambiguous path: once with no branch, which the server
   * answers with the candidate letterheads instead of creating anything, and
   * again with the one the agent picked in the modal. That is a question rather
   * than an error — the RENs sit in different branches and the system refuses to
   * guess whose letterhead a signed fee agreement prints on.
   */
  const raiseCoShareForm = async (branchId = null) => {
    const r = await coShareFormsApi.createSaleCoShareForm(id, branchId ? { branch_id: branchId } : {});
    const out = r?.data ?? r;
    if (out?.needs_branch) {
      setBranchPick(out.preferred_branch_id || out.choices?.[0]?.id || null);
      setBranchAsk(out);
      return;
    }
    setBranchAsk(null);
    await Promise.all([loadCoShareForm(), loadAtts()]);
    if (out?.id) nav(`/co-share-forms/${out.id}`);
  };
  const loadFinance = () => salesApi.getSalesFinanceById(id).then((r) => setFin(r?.data ?? r)).catch((e) => message.error(e.message));
  // Entries and payees are re-read together after every write: saving an entry with a
  // new bank account binds that account to the party, so the picker is stale the
  // moment the sheet closes.
  const loadBalanceTo = () => salesApi.listSalesBalanceTo(id).then((r) => setBt(r?.data ?? r)).catch((e) => message.error(e.message));
  const loadBalancePayees = () => salesApi.listSalesBalanceToPayees(id).then((r) => setBtPayees(r?.data ?? r)).catch((e) => message.error(e.message));

  /* Re-read this sale when something changed it behind the agent's back — HQ
     raising an invoice, finance validating a receipt. Re-reads in place so the
     screen updates without losing scroll or the open tab. */
  useEffect(() => {
    const onRefresh = () => { load(); if (fin) loadFinance(); if (bt) { loadBalanceTo(); loadBalancePayees(); } };
    window.addEventListener('cid:refresh', onRefresh);
    return () => window.removeEventListener('cid:refresh', onRefresh);
  }); // no dep array: `load`/`fin`/`bt` are re-created each render and must stay current
  // Best-effort: a badge is nice to have, so a failure here shows no badge
  // rather than erroring the page.
  const loadNoteUnread = () => salesApi.getSalesNotesUnreadCount(id)
    .then((r) => setNoteUnread((r?.data ?? r)?.count || 0))
    .catch(() => {});
  useEffect(() => { setFin(null); setCb(null); setCbErr(false); setBt(null); setBtPayees(null); load(); loadAtts(); loadNoteUnread(); loadCoShareForm(); /* eslint-disable-next-line */ }, [id]);
  // Lazy-load the money view the first time the Finance tab is opened.
  useEffect(() => { if ((seg === 'finance' || seg === 'invoices') && !fin) loadFinance(); /* eslint-disable-next-line */ }, [seg]);
  useEffect(() => { if (seg === 'balance' && !bt) { loadBalanceTo(); loadBalancePayees(); } /* eslint-disable-next-line */ }, [seg]);
  /**
   * The breakdown is re-read every time the tab is opened, not cached for the
   * life of the page.
   *
   * Before the first invoice these numbers are an estimate derived from the
   * sale's live figures and agent splits, so a cached copy is simply wrong the
   * moment either changes — edit the split on the Parties tab, come back, and
   * the old caching showed the pre-edit split forever. It also strands the
   * failure case: one look while the projection was unavailable pinned
   * "commissions aren't calculated yet" to the tab for the rest of the session,
   * with no way back short of reloading the app.
   *
   * The previous answer stays on screen while the new one is in flight, so a tab
   * switch doesn't flash skeletons over money that is already drawn.
   */
  useEffect(() => {
    if (seg !== 'commissions') return undefined;
    let alive = true;
    // scope=mine: this tab shows the agent their own commission and nothing else,
    // so the other recipients' amounts never leave the server.
    salesApi.getSalesCommissionBreakdownById(id, { scope: 'mine' })
      .then((r) => { if (alive) { setCb(r?.data ?? r); setCbErr(false); } })
      .catch(() => { if (alive) setCbErr(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg, id, cbTick]);

  // A `/receipt` or `/attachment` link inside a comment jumps to that tab —
  // same affordance as the admin drawer's navigateToSaleTab event.
  const openResource = (kind, _resourceId) => {
    setNotesOpen(false);
    setSeg(kind === 'receipt' ? 'receipts' : 'attachments');
  };

  /* ── Cancel / delete ──
     Both mirror the admin sale workspace: a sale converted from a booking form
     asks what should happen to that form, and the delete asks the agent to type
     the reference back before it will run. `sale.danger` carries the server's
     view of what is possible and why — see sale.service._dangerZone. */
  const danger = sale?.danger || null;
  const linkedForm = danger?.booking_form || null;

  const [cxOpen, setCxOpen] = useState(false);       // cancel (void) sheet
  const [cxForm, setCxForm] = useState(false);       // "also cancel the booking form"
  const [cxReason, setCxReason] = useState('');
  const [delOpen, setDelOpen] = useState(false);     // delete sheet
  const [delForm, setDelForm] = useState(false);
  const [delReason, setDelReason] = useState('');
  const [delRef, setDelRef] = useState('');
  const delRefOk = delRef.trim().toUpperCase() === String(sale?.ref || '').toUpperCase();

  const openCancel = () => { setCxForm(false); setCxReason(''); setCxOpen(true); };
  const openDiscard = () => { setDelForm(false); setDelReason(''); setDelRef(''); setDelOpen(true); };

  // Agent-side cancel — Pending only (server-enforced): mirrors the admin void
  // outcome for that state (listing back to Active unless a surviving booking form
  // still holds it, reserved unit released).
  const cancelSale = async () => {
    if (cxForm && !cxReason.trim()) { message.warning('Cancellation reason is required'); return; }
    await salesApi.createSalesVoidById(id, cxForm ? { cancel_booking_form: true, cancellation_reason: cxReason.trim() } : {});
    message.success(cxForm ? 'Sale cancelled and booking form cancelled' : 'Sale cancelled');
    setCxOpen(false);
    await load();
  };

  // Hard delete — the row and its junctions are gone, not archived. The heaviest of
  // the destructive actions, so it asks for the reference in the agent's own hand
  // and points at Cancel as the alternative for a deal that merely fell through.
  const discard = async () => {
    if (!delRefOk) { message.warning(`Type ${sale?.ref} to confirm`); return; }
    if (delForm && !delReason.trim()) { message.warning('Cancellation reason is required'); return; }
    await salesApi.deleteSalesById(id, delForm
      ? { data: { cancel_booking_form: true, cancellation_reason: delReason.trim() } }
      : {});
    message.success(delForm ? 'Sale deleted and booking form cancelled' : 'Sale deleted');
    setDelOpen(false);
    // The sale is gone, so nothing still typed on this page — parties, a note —
    // is worth stopping the move for.
    partiesGuard.allowOnce();
    nav('/sales');
  };

  // Record a payment receipt (Converted/Completed sale) → enters HQ finance validation.
  const recordReceipt = async () => {
    if (!rc.fee_type) { message.warning('Fee type is required'); return; }
    if (rc.fee_type === 'OTHER' && !rc.fee_type_other.trim()) { message.warning('Describe the fee type'); return; }
    if (!(parseFloat(rc.amount) > 0)) { message.warning('Enter a valid receipt amount'); return; }
    if (!rc.receipt_date) { message.warning('Receipt date is required'); return; }
    try {
      await salesApi.createSalesReceiptsById(id, {
        fee_type: rc.fee_type,
        fee_type_other: rc.fee_type === 'OTHER' ? rc.fee_type_other.trim() : undefined,
        payer_type: rc.payer_type,
        payer_name: rc.payer_name.trim() || undefined,
        amount: Number(rc.amount),
        receipt_date: rc.receipt_date ? rc.receipt_date.format('YYYY-MM-DD') : undefined,
        notes: rc.notes.trim() || undefined,
        attachment_ids: rc.attachment_ids,
      });
    } catch (e) {
      // A stamping fee is reconciled against Tenancy fee collected, which lives on
      // the edit screen. Naming the missing field and leaving the agent to find it
      // is where they get stuck, so offer the jump — the same guidance the admin
      // portal gives (SaleReceiptsTab -> focusOverviewField).
      const text = e?.message || '';
      // Two different refusals point at the same field: it is blank, or it
      // disagrees with the receipt. Both are fixed in the same place, so both
      // offer the jump — but they are not the same problem, so title them apart.
      const notRecorded = /record the tenancy fee collected/i.test(text);
      const mismatch = /tenancy fee collected is rm/i.test(text);
      if (notRecorded || mismatch) {
        modal.confirm({
          title: notRecorded ? 'Tenancy fee not recorded yet' : 'Amounts do not match',
          content: text,
          okText: 'Take me there',
          cancelText: 'Stay here',
          // The agent chose to go and fix it, so the receipt's own leave warning
          // would only ask the same question twice.
          onOk: () => { rcGuard.release(); setRcOpen(false); nav(`/sales/${id}/edit?focus=tenancy_fee`); },
        });
        return;
      }
      throw e;
    }
    message.success('Receipt recorded — pending HQ validation');
    setRc(emptyRc());
    setRcDirty(false);
    setRcOpen(false);
    load();
  };
  // Removing a receipt can't be undone and pulls a payment HQ finance may already be
  // looking at, so the agent acknowledges exactly which one goes first — same gate as
  // the admin's delete-receipt modal (SaleReceiptsTab).
  const removeReceipt = (r) => {
    const slips = (r.attachments || []).length;
    modal.confirm({
      title: 'Remove this receipt?',
      content: (
        <span>
          <b>{r.label}</b> of <b>{r.amount}</b>{r.payer ? ` from ${r.payer}` : ''} will be withdrawn
          from HQ finance validation. This cannot be undone — you would have to record it again.
          {slips > 0 && ` The ${slips === 1 ? 'bank-in slip stays' : `${slips} bank-in slips stay`} under Documents.`}
        </span>
      ),
      okText: 'Remove receipt',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await salesApi.deleteSalesReceiptsByIdReceiptId(id, r.id);
        } catch (e) {
          // Nothing toasts errors globally — surface it, then rethrow so the modal
          // stays open on a server refusal (e.g. finance validated it a moment ago).
          message.error(e.message);
          throw e;
        }
        message.success('Receipt removed');
        load();
      },
    });
  };

  /* ── Balance To ──────────────────────────────────────────────────────────────
     A balance is a DECLARATION: it tells finance who to pay out of the client
     account and into which account. Nothing here moves money — the payment itself
     is still a refund voucher HQ raises. */

  // Open the sheet to add (no entry) or to edit one. Editing pre-fills all five
  // steps, the bank account included, so the agent corrects a figure rather than
  // re-answering the whole form.
  const openBalance = (b) => {
    setBtEdit(b || null);
    // A lawyer points at no contact and no saved account, so the name and the account
    // load back into the same typed fields that wrote them — there is nothing to
    // re-select them from, and the entry's own snapshot is the whole record.
    const freeText = !!b && !b.party_id;
    setBf(b ? {
      // Defaulted rather than read raw: an entry written before the column
      // existed carries no kind, and it was a balance.
      entry_type: b.entry_type || 'BALANCE_TO',
      party_type: b.party_type,
      party_id: freeText ? undefined : b.party_id,
      party_name: freeText ? (b.party_name || '') : '',
      amount: Number(b.amount || 0).toFixed(2),
      bank_account_id: freeText ? undefined : b.bank_account_id,
      bank_name: freeText ? (b.bank_name || '') : '',
      account_no: freeText ? (b.bank_account_no || '') : '',
      account_holder_name: freeText ? (b.bank_account_holder || '') : '',
      remarks: b.remarks || '',
    } : emptyBf());
    setBtDirty(false);
    setBtOpen(true);
  };

  // The group / party / accounts the form currently points at. A party with nothing
  // on file skips the picker entirely — there is nothing to pick from — and goes
  // straight to the fields that create one.
  const btGroup = (btPayees || []).find((g) => g.party_type === bf.party_type) || null;
  // A lawyer is not a party on the deal: nothing to pick, nothing saved to pick from,
  // so the name is typed and the bank fields are always the typed ones. While the
  // payees are still loading there is no group to read `free_text` off, but an entry
  // pointing at no contact is already known to be one — editing a lawyer must not
  // flash a party picker it will never use.
  const btFreeText = btGroup ? !!btGroup.free_text : (!!btEdit && !btEdit.party_id);
  const btParty = (btGroup?.parties || []).find((p) => p.id === bf.party_id) || null;
  const btBanks = btParty?.bank_accounts || [];
  // The sentinel counts on its own, not only while a party is resolved: if the payee
  // is taken off the sale while the sheet is open (HQ edits the parties, cid:refresh
  // re-reads the payees) btParty goes null, and '__new__' must not fall through to the
  // saved-account branch — that posts the sentinel as a bank_account_id.
  const btNewBank = btFreeText || bf.bank_account_id === NEW_BANK || (!!btParty && btBanks.length === 0);
  // Who the account holder falls back to: the contact that was picked, or — for a
  // lawyer — whatever name has been typed so far.
  const btPayeeName = btFreeText ? bf.party_name.trim() : (btParty?.name || '');
  // The picker only knows the party's accounts as they stand now; an entry raised
  // against one that has since been renamed still has to show what it was raised on.
  const btSelectedBank = btEdit && bf.party_id === btEdit.party_id && bf.bank_account_id === btEdit.bank_account_id
    ? bankLine(btEdit.bank_name, btEdit.bank_account_no, btEdit.bank_account_holder)
    : undefined;

  // Only what the client can answer on its own is checked here. Whether the party is
  // on the sale, whether the account is theirs, whether the sale is still writable —
  // all the server's, and its refusals are written for the agent, so they surface as
  // they come back.
  const saveBalance = async () => {
    if (!bf.party_type) { message.warning(`Choose who the ${balanceNoun(bf.entry_type)} is paid to`); return; }
    // A lawyer is named, not picked — there is no record on the sale to point at.
    if (btFreeText) {
      if (!bf.party_name.trim()) { message.warning('Enter the lawyer’s name'); return; }
    } else if (!bf.party_id) { message.warning('Choose who to pay'); return; }
    if (!(Number(bf.amount) > 0)) { message.warning('Enter an amount greater than zero'); return; }
    // The keypad lets more decimals through than money has; the server rounds them
    // away silently, which is the one thing a payment instruction must not do.
    if (!/^\d*(\.\d{0,2})?$/.test(bf.amount)) { message.warning('Amounts go to two decimal places'); return; }
    const body = {
      entry_type: bf.entry_type,
      party_type: bf.party_type,
      // The server takes a typed name for a lawyer and no party_id; for the three
      // contact-backed kinds it is the other way round.
      ...(btFreeText ? { party_name: bf.party_name.trim() } : { party_id: bf.party_id }),
      amount: Number(bf.amount),
      remarks: bf.remarks.trim() || undefined,
    };
    if (btNewBank) {
      if (!bf.bank_name.trim()) { message.warning('Bank name is required'); return; }
      if (!bf.account_no.trim()) { message.warning('Account number is required'); return; }
      body.new_bank_account = {
        bank_name: bf.bank_name.trim(),
        account_no: bf.account_no.trim(),
        account_holder_name: bf.account_holder_name.trim() || undefined,
      };
    } else {
      if (!bf.bank_account_id) { message.warning('Select a bank account, or add one for this party'); return; }
      body.bank_account_id = bf.bank_account_id;
    }
    if (btEdit) await salesApi.updateSalesBalanceTo(id, btEdit.id, body);
    else await salesApi.createSalesBalanceTo(id, body);
    message.success(btEdit ? 'Entry updated' : 'Entry added');
    setBtDirty(false);
    setBtOpen(false);
    setBtEdit(null);
    setBf(emptyBf());
    await Promise.all([loadBalanceTo(), loadBalancePayees()]);
  };

  // Removing an instruction is undone by adding it again — it is the payment being
  // withdrawn from finance's list, not money being taken back — so the confirm names
  // the figure and the payee and stops there.
  const removeBalance = (b) => {
    modal.confirm({
      title: `Remove this ${balanceNoun(b.entry_type)}?`,
      content: (
        <span>
          <b>{RM(b.amount)}</b> to <b>{b.party_name}</b> comes off this sale, so HQ finance
          won&rsquo;t pay it out. You can add it again.
        </span>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await salesApi.deleteSalesBalanceTo(id, b.id);
        } catch (e) {
          message.error(e.message);
          throw e; // keep the modal open on a server refusal
        }
        message.success('Entry removed');
        loadBalanceTo();
      },
    });
  };

  // Signed-URL upload: get URL → PUT bytes straight to Supabase Storage (bare axios,
  // no app interceptors) → confirm so the server persists the row → refresh the list.
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const ct = file.type || 'application/octet-stream';
      const up = (await salesApi.createSalesAttachmentsUploadUrlById(id, { fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
      if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
      try {
        await axios.put(up.uploadUrl, file, { headers: { 'Content-Type': ct, 'x-upsert': 'false' } });
      } catch {
        throw new Error('Could not upload this file — please use a PDF or a JPG / PNG / WebP image (max 10 MB).');
      }
      await salesApi.createSalesAttachmentsConfirmUploadById(id, {
        storagePath: up.path, fileName: file.name, fileSize: file.size, contentType: ct, category: pendingCat.current,
      });
      message.success('Document uploaded');
      await loadAtts();
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // One endpoint, two meanings. On an ATP_ATR row the server unlinks the booking form
  // and frees it; on any other row it deletes the file from storage for good. Both are
  // confirmed: deleting a document cannot be undone, and unlinking undoes the
  // conversion that tied a form to this sale. Neither was confirmed before — a single
  // mis-tap destroyed an uploaded document with no warning at all.
  const confirmRemove = async () => {
    const a = removeTarget;
    const isFormLink = !!a.booking_form_id;
    await salesApi.deleteSalesAttachmentsByIdAttachmentId(id, a.id);
    message.success(isFormLink ? 'Booking form unlinked' : 'Document deleted');
    setRemoveTarget(null);
    await loadAtts();
    // Unlinking changes the sale's own header (its linked form is gone), so the sale
    // is reloaded too. A plain delete touches nothing but the document list.
    if (isFormLink) await load();
  };

  // The extension the saved file should carry. A SQL invoice is always a PDF,
  // but a manually recorded one is whatever finance uploaded — often a photo of
  // the branch's own invoice — so trust the blob's own type over assuming .pdf.
  const docExt = (blob) => ({
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
  }[blob?.type] || '.pdf');

  // Download an issued invoice's document (proxied read-only → Blob → save).
  const downloadInvoicePdf = async (iv) => {
    const blob = await salesApi.getSalesInvoicePdfById(id, iv.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${iv.doc_no || 'invoice'}${docExt(blob)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Bank-in slips already uploaded on this sale — linkable to a receipt.
  const bankSlips = atts.filter((a) => a.category === 'BANK_IN_SLIP').map((a) => ({ value: a.id, label: a.file_name }));

  // ── In-place parties management (mirrors the admin Parties tab) ──────────────
  // vendors/purchasers: editable while Pending OR Converted (edit_level !== 'none');
  // agents & splits: Pending only (edit_level === 'full') — in place here. Never
  // below one row per group. An invoiced sale ('invoiced') changes its split and
  // co-agencies on the Edit screen instead, through HQ's "what changes" check
  // (owner, 2026-10-09); here they read only, as at 'limited'.
  const [pm, setPm] = useState(null);
  // Unsaved parties edits. The tab's load fills `pm` through setPm; every edit the
  // agent makes goes through editPm, and only that marks it.
  const [pmDirty, setPmDirty] = useState(false);
  const editPm = (fn) => { setPmDirty(true); setPm(fn); };
  const pmFull = sale?.edit_level === 'full';
  // A rental's two sides are a landlord and a tenant, not a vendor and a purchaser —
  // same tables, same payload keys, different words. See lib/partyWords.
  const words = partyWords(sale?.type);
  useEffect(() => {
    if (seg !== 'parties' || !sale || sale.edit_level === 'none' || pm) return;
    salesApi.getSalesEditById(id).then((r) => {
      const d = r?.data ?? r;
      setPm({
        vendors: (d.vendors || []).map((v) => ({ id: v.id, name: v.name })),
        purchasers: (d.purchasers || []).map((p) => ({ id: p.id, name: p.name })),
        agents: (d.agents || []).map((a) => ({ agent_id: a.agent_id, name: a.name, split_percentage: Number(a.split_percentage) || 0, self: a.agent_id === me?.id })),
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg, sale]);

  // One paged lookup per picker. These are caller-scoped server searches, so a single
  // capped page hid real contacts behind a list that stopped dead at the first screen;
  // the sheets now scroll the next page in.
  //
  // Rows already on the sale ride along greyed rather than being filtered out — a
  // filtered page arrives short, and a short page is how usePagedOptions decides the
  // feed has ended, so filtering here would cut pagination off early.
  const vendorLookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({ type: 'vendor', q, page, limit: CONTACT_PAGE });
    return ((r?.data ?? r) || []).map((c) => ({ value: c.id, label: c.name, sub: [c.registration_no, c.contact_no || c.phone].filter(Boolean).join(' · ') || undefined, data: c }));
  }, CONTACT_PAGE);
  const purchaserLookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({ type: 'purchaser', q, page, limit: CONTACT_PAGE });
    return ((r?.data ?? r) || []).map((c) => ({ value: c.id, label: c.name, sub: [c.registration_no, c.contact_no || c.phone].filter(Boolean).join(' · ') || undefined, data: c }));
  }, CONTACT_PAGE);
  const agentLookup = usePagedOptions(async (q, page) => {
    const r = await teamApi.listAgentsLookup({ q, page, limit: CONTACT_PAGE });
    return ((r?.data ?? r) || []).map((a) => ({ value: a.id, label: a.name, sub: [a.ren_no ? `REN ${a.ren_no}` : null, a.management_rank].filter(Boolean).join(' · ') || undefined, data: a }));
  }, CONTACT_PAGE);
  const partyLookup = { vendor: vendorLookup, purchaser: purchaserLookup, agent: agentLookup };

  const addParty = (kind, cid) => {
    const opt = partyLookup[kind].options.find((o) => o.value === cid);
    if (!opt) return;
    if (kind === 'agent') editPm((s) => ({ ...s, agents: [...s.agents, { agent_id: cid, name: opt.data.name, split_percentage: 0, self: false }] }));
    else editPm((s) => ({ ...s, [`${kind}s`]: [...s[`${kind}s`], { id: cid, name: opt.data.name }] }));
  };

  // Resolves true once saved, false when a check stopped it — the leave guard's
  // "Save & leave" stays put on false, the warning having said why.
  const savePartyChanges = async () => {
    if (!pm.vendors.length) { message.warning(`At least one ${words.vendor.toLowerCase()} is required`); return false; }
    if (!pm.purchasers.length) { message.warning(`At least one ${words.purchaser.toLowerCase()} is required`); return false; }
    if (pmFull) {
      const tot = pm.agents.reduce((s, a) => s + (Number(a.split_percentage) || 0), 0);
      if (Math.abs(tot - 100) > 0.01) { message.warning(`Agent splits must total 100% (currently ${tot}%)`); return false; }
    }
    try {
      await salesApi.updateSalesById(id, {
        vendors: pm.vendors.map((v) => ({ vendor_id: v.id })),
        purchasers: pm.purchasers.map((p) => ({ purchaser_id: p.id })),
        ...(pmFull ? { agents: pm.agents.map((a) => ({ agent_id: a.agent_id, split_percentage: Number(a.split_percentage) || 0 })) } : {}),
      });
    } catch (e) {
      // The server refused the change (a 4xx — e.g. a landlord a Balance To row still
      // pays): nothing was saved, so drop the edits and reload what is, rather than
      // leave the list showing a party removed that is still on the sale. A dropped
      // connection or a 5xx keeps the edits for another try. The caller shows e.
      if (e?.status >= 400 && e?.status < 500) {
        setPmDirty(false);
        setPm(null);
        load();
      }
      throw e;
    }
    message.success('Parties updated');
    setPmDirty(false);
    setPm(null); // refetch fresh rows next time the tab loads
    load();
    return true;
  };

  const partiesGuard = useUnsavedChanges(!!pm && pmDirty, {
    what: 'the parties',
    saveLabel: 'Save & leave',
    onSave: async () => {
      try {
        return await savePartyChanges();
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
  });

  // Upload a NEW bank-in slip from inside the receipt form (admin parity): same
  // signed-URL flow as attachments but pinned to category BANK_IN_SLIP, and the new
  // attachment auto-links onto the receipt being recorded.
  const slipRef = useRef(null);
  const [slipUploading, setSlipUploading] = useState(false);
  const handleSlipFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSlipUploading(true);
    try {
      const ct = file.type || 'application/octet-stream';
      const up = (await salesApi.createSalesAttachmentsUploadUrlById(id, { fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
      if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
      await axios.put(up.uploadUrl, file, { headers: { 'Content-Type': ct, 'x-upsert': 'false' } });
      const confRes = await salesApi.createSalesAttachmentsConfirmUploadById(id, {
        storagePath: up.path, fileName: file.name, fileSize: file.size, contentType: ct, category: 'BANK_IN_SLIP',
      });
      const conf = confRes?.data ?? confRes;
      await loadAtts();
      // Functional, not setRcv: the upload took a while and the agent may have
      // linked other slips meanwhile.
      if (conf?.id) { setRcDirty(true); setRc((s) => ({ ...s, attachment_ids: [...s.attachment_ids, conf.id] })); }
      message.success('Bank-in slip uploaded & linked');
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setSlipUploading(false);
    }
  };

  // Invoice PDF (read-only — HQ issues invoices): authenticated blob fetch → in-app viewer.
  const viewInvoicePdf = async (inv) => {
    const r = await salesApi.getSalesInvoicePdfById(id, inv.id);
    const raw = r instanceof Blob ? r : (r?.data instanceof Blob ? r.data : r);
    const blob = raw instanceof Blob ? raw : new Blob([raw], { type: 'application/pdf' });
    setFileView({ name: `${inv.doc_no || 'invoice'}${docExt(blob)}`, url: URL.createObjectURL(blob) });
  };

  // The customer's OFFICIAL RECEIPT for a payment HQ knocked off an invoice — the
  // one the customer asks the agent for, and the one thing on the money side the
  // app used to be unable to show. Same blob → viewer path as the invoice.
  const viewPaymentPdf = async (pmt) => {
    const r = await salesApi.getSalesPaymentPdfById(id, pmt.id);
    const raw = r instanceof Blob ? r : (r?.data instanceof Blob ? r.data : r);
    const blob = raw instanceof Blob ? raw : new Blob([raw], { type: 'application/pdf' });
    setFileView({ name: `${pmt.or_no || 'receipt'}${docExt(blob)}`, url: URL.createObjectURL(blob) });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title={sale?.ref || 'Sale'}
        sub={sale ? `${sale.type} · ${sale.priceF}` : ''}
        right={sale ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {sale.edit_level && sale.edit_level !== 'none' && <span onClick={() => nav(`/sales/${id}/edit`)} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 13, fontWeight: 600 }}>Edit</span>}
            <Chip label={sale.status} />
          </div>
        ) : null}
      />
      {!sale ? (
        <div style={{ padding: 18 }}><Skeletons n={3} height={70} /></div>
      ) : (
        <>
          {/* Module tabs — scrollable chips (7+ modules don't fit squeezed on a phone) */}
          <div className="scrx" style={{ display: 'flex', gap: 8, padding: '14px 15px', overflowX: 'auto', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
            {SEGS.map((s) => (
              <div key={s} data-tour={`sale-tab-${s}`} onClick={() => setSeg(s)} style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', background: seg === s ? 'var(--primary)' : 'var(--surface2)', color: seg === s ? '#fff' : 'var(--muted)', border: `1px solid ${seg === s ? 'var(--primary)' : 'var(--border)'}` }}>
                {SEG_LABEL[s]}
              </div>
            ))}
          </div>

          {/* Bottom padding clears the notes FAB (52px + its 20px offset) so the
              last row of any tab — a Record receipt button, a document's Delete —
              can still be scrolled out from under it. */}
          <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px calc(84px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {seg === 'overview' && (
              <>
                {/* Stage as a STATE, not a step.
                    The old strip drew eleven chips left-to-right with everything before
                    the current one shaded "done" — a history nothing records, on a path
                    that doesn't exist (no server enforces any order between stages). So
                    this states what is true now and who holds it, and puts the changes an
                    agent may make behind one control.

                    The stage colour rides on a dot, not a rail down the card's edge: an
                    accent stripe on a rounded card is decoration, it fought the corner
                    radius, and it said nothing the dot doesn't. */}
                <div data-tour="sale-stage-card" style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: STAGE_COLOR[sale.stageLabel] || '#8c8c8c' }} />
                        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' }}>STAGE</span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3 }}>{sale.stageLabel}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                        {sale.stage_locked_by_hq
                  ? 'locked: HQ has this claim, only HQ can change the stage'
                  : (sale.settable_stages || []).includes(sale.stage_raw) ? 'you set this' : 'HQ sets this'}
                      </div>
                    </div>
                    {(sale.settable_stages || []).length > 0 && (
                      <Button data-tour="sale-stage-change" size="small" onClick={() => setStageSheet(true)} style={{ flex: 'none' }}>Change</Button>
                    )}
                  </div>
                  {sale.editable && (
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, borderTop: '1px solid var(--border)', paddingTop: 9 }}>
                      With HQ for review — you can keep editing until they convert it, after
                      which only property info, dates, remarks and your booking form no. stay open.
                    </div>
                  )}
                </div>

                <Card>
                  <KV k="Reference" v={sale.ref} mono />
                  {/* The agent's own pad number, right under our ref because the two
                      are read together — one identifies the deal to CID, the other
                      to them. Hidden when there is none: most sales have none, and a
                      row of dashes on every one of them says nothing. */}
                  {sale.agent_booking_form_no && (
                    <KV k="Your booking form no." v={sale.agent_booking_form_no} mono />
                  )}
                  {/* HQ's case number and the ATP / ATR on file: the two numbers HQ
                      quotes when it calls about a deal. Always shown, unlike the pad
                      number above — "not yet" is the answer the agent is looking for
                      when there is none. Read-only: HQ keys the case number in. */}
                  <KV k="Case no." v={sale.case_number || <Muted>Not assigned yet</Muted>} mono={!!sale.case_number} />
                  {(() => {
                    const { agreements, authorisations } = splitLinkedForms(sale.booking_forms);
                    const none = sale.booking_forms === null ? <Muted>Couldn&rsquo;t load</Muted> : <Muted>None linked</Muted>;
                    return (
                      <>
                        <KV k={agreementLabel(sale.type === 'Rent')} v={agreements.length ? <FormRefs forms={agreements} nav={nav} /> : none} />
                        {authorisations.length > 0 && (
                          <KV k="Authorisation letter" v={<FormRefs forms={authorisations} nav={nav} />} />
                        )}
                      </>
                    );
                  })()}
                  <KV k="Property" v={sale.title} />
                  <KV k="Type" v={sale.type} />
                </Card>
                {sale.financials && (
                  <>
                    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>FINANCIAL</div>
                    <Card>
                      <KV k="Sale date" v={sale.financials.sale_date || '—'} />
                      <KV k={sale.type === 'Rent' ? 'Monthly rental' : 'Gross sales'} v={RM(sale.financials.gross_sales_amount)} />
                      <KV k="Commission" v={`${sale.financials.commission_percentage ?? '—'}%  ·  ${RM(sale.financials.total_commission)}`} />
                      <KV k="SST" v={`${sale.financials.vendor_pay_tax ? `${words.vendor} pays` : 'CID absorbs'}  ·  ${sale.financials.sst_rate ?? '—'}%  ·  ${RM(sale.financials.sst_amount)}`} />
                      <KV k={sale.financials.cid_net_gci != null ? 'Net GCI (whole deal)' : 'Net GCI'} v={RM(sale.financials.net_gci)} />
                      {/* "They list" — the deal above is what the other agency
                          banks. This is the figure the splits and your GCI run on. */}
                      {sale.financials.cid_net_gci != null && (
                        <>
                          <KV k={`Less: ${sale.financials.lister_name || 'they'} keeps`} v={`−${RM(sale.financials.lister_retains)}`} />
                          <KV k="CID's portion" v={RM(sale.financials.cid_net_gci)} />
                        </>
                      )}
                    </Card>
                  </>
                )}

                {/* Destructive actions live at the BOTTOM, after everything you'd read
                    first — they used to open the page, so the first thing an agent saw
                    on their own sale was two red buttons. They are also two genuinely
                    different things that the old labels ("Cancel sale" / "Discard") did
                    nothing to distinguish, so each now says what it leaves behind. */}
                {(danger?.can_cancel || danger?.can_delete) && (
                  <>
                    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 6 }}>IF THIS DEAL IS OFF</div>
                    <div style={{ ...rowCard, padding: 0, overflow: 'hidden' }}>
                      {danger.can_cancel && (
                        <DangerRow
                          label="Cancel this sale"
                          sub={linkedForm
                            ? `Keeps the record, marks it Voided. You'll be asked what to do with booking form ${linkedForm.ref}.`
                            : 'Keeps the record, marks it Voided. The linked listing goes back to Active and any reserved unit is released.'}
                          blocked={danger.cancel_blocked_reason}
                          onClick={openCancel}
                          onError={(e) => message.error(e.message)}
                        />
                      )}
                      {danger.can_delete && (
                        <DangerRow
                          label="Delete permanently"
                          sub="Removes the sale and everything on it. Use this only for one keyed in by mistake."
                          blocked={danger.delete_blocked_reason}
                          onClick={openDiscard}
                          onError={(e) => message.error(e.message)}
                          last
                        />
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {seg === 'parties' && (
              <>
                {sale.editable && (
                  <div style={{ ...rowCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>Add or change agents, splits &amp; parties</span>
                    <Button size="small" type="primary" onClick={() => nav(`/sales/${id}/edit`)}>Edit</Button>
                  </div>
                )}

                {/* Agents & commission split — managed while Pending (like admin); locked at conversion. */}
                <div style={sectionLabel}>AGENTS &amp; COMMISSION SPLIT</div>
                {pm && pmFull ? (
                  <Card>
                    {pm.agents.map((a, i, arr) => (
                      <div key={a.agent_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}{a.self ? ' (me)' : ''}</div>
                          <AgentBranch agent={(sale.parties?.agents || []).find((x) => x.agent_id === a.agent_id)} />
                        </div>
                        <Input type="number" min={0} max={100} value={a.split_percentage} suffix="%" style={{ width: 92, flex: 'none' }}
                          onChange={(e) => editPm((s) => ({ ...s, agents: s.agents.map((x) => (x.agent_id === a.agent_id ? { ...x, split_percentage: e.target.value === '' ? '' : Number(e.target.value) } : x)) }))} />
                        <Button size="small" danger disabled={arr.length <= 1}
                          onClick={() => editPm((s) => ({ ...s, agents: s.agents.filter((x) => x.agent_id !== a.agent_id) }))}>✕</Button>
                      </div>
                    ))}
                    <div style={{ padding: '10px 0' }}>
                      <SheetSelect
                        title="Add a co-agent"
                        placeholder="＋ Add co-agent — search name / REN…"
                        searchPlaceholder="Search name or REN no…"
                        value={null}
                        onSearch={agentLookup.search}
                        onChange={(v) => addParty('agent', v)}
                        options={markPicked(agentLookup.options, pm.agents.map((x) => x.agent_id))}
                        onLoadMore={agentLookup.loadMore}
                        hasMore={agentLookup.hasMore}
                        loadingMore={agentLookup.loadingMore}
                        notFoundContent="No agent matches that name or REN."
                      />
                    </div>
                  </Card>
                ) : (
                  <Card>
                    {(sale.parties?.agents || []).length === 0 ? (
                      <div style={{ padding: '11px 0', fontSize: 13, color: 'var(--muted)' }}>No agents linked to this sale.</div>
                    ) : (
                      sale.parties.agents.map((a, i, arr) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, background: a.self ? 'var(--navy)' : 'var(--chip)', color: a.self ? '#fff' : 'var(--primary)' }}>{initials(a.name)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                            {a.ren && <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{renLabel(a.ren)}</div>}
                            <AgentBranch agent={a} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flex: 'none' }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2.5px 8px', borderRadius: 999, background: 'var(--chip)', color: 'var(--primary)', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>SALE AGENT{a.self ? ' · ME' : ''}</span>
                            <span style={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{a.split_percentage != null ? `${a.split_percentage}% split` : '—'}</span>
                          </div>
                        </div>
                      ))
                    )}
                    {sale.edit_level === 'limited' && (
                      <div style={{ padding: '9px 0', fontSize: 11.5, color: 'var(--muted)' }}>Agent splits are locked after conversion — contact HQ to change them.</div>
                    )}
                    {/* Invoiced: they still change, from Edit, through HQ's "what changes" check. */}
                    {sale.edit_level === 'invoiced' && (
                      <div style={{ padding: '9px 0', fontSize: 11.5, color: 'var(--muted)' }}>Change co-agencies, referrals and splits from Edit — you&rsquo;ll see what changes before it&rsquo;s saved.</div>
                    )}
                  </Card>
                )}

                <div style={sectionLabel}>PARTIES</div>
                {pm ? (
                  <>
                    {[['vendors', 'vendor', words.vendors, words.vendor], ['purchasers', 'purchaser', words.purchasers, words.purchaser]].map(([listKey, kind, label, one]) => (
                      <Card key={listKey}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', padding: '9px 0 3px' }}>{label.toUpperCase()}</div>
                        {pm[listKey].map((p, i, arr) => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                            <Button size="small" danger disabled={arr.length <= 1}
                              onClick={() => editPm((s) => ({ ...s, [listKey]: s[listKey].filter((x) => x.id !== p.id) }))}>✕</Button>
                          </div>
                        ))}
                        <div style={{ padding: '9px 0' }}>
                          <SheetSelect
                            title={`Add a ${one.toLowerCase()}`}
                            placeholder={`＋ Add ${one.toLowerCase()} — search name…`}
                            searchPlaceholder={`Search ${label.toLowerCase()} by name or IC…`}
                            value={null}
                            onSearch={partyLookup[kind].search}
                            onChange={(v) => addParty(kind, v)}
                            options={markPicked(partyLookup[kind].options, pm[listKey].map((x) => x.id))}
                            onLoadMore={partyLookup[kind].loadMore}
                            hasMore={partyLookup[kind].hasMore}
                            loadingMore={partyLookup[kind].loadingMore}
                            notFoundContent={`No ${one.toLowerCase()} matches. Create one from the Contacts tab first.`}
                          />
                        </div>
                      </Card>
                    ))}
                    <AsyncButton type="primary" block onClick={savePartyChanges} onError={(e) => message.error(e.message)}>Save parties</AsyncButton>
                  </>
                ) : (
                  <Card>
                    <KV k={words.vendor} v={sale.parties?.vendor} />
                    <KV k={words.purchaser} v={sale.parties?.purchaser} />
                    <KV k="Co-agency" v={sale.parties?.coAgency} />
                  </Card>
                )}

                {(sale.parties?.coAgencies || []).length > 0 && (
                  <Card>
                    {sale.parties.coAgencies.map((c, i, arr) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{c.role_label}</div>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                          {/* Reads the row's basis AND its side of the deal — a
                              flat-sum co-agency has no percentage, and a "They
                              list" row's own share is 0 because they are the ones
                              holding the fee. An after-co-agency referral
                              also gets its RM, priced two-pass off the other
                              rows as the engine prices it. */}
                          {rowShareLabel(c, undefined, { rows: arr, netGci: sale.financials?.net_gci })}
                        </span>
                      </div>
                    ))}
                  </Card>
                )}
              </>
            )}
            {seg === 'commissions' && (() => {
              const rows = sale.commissions || [];
              const totalN = rows.reduce((s, c) => s + (c.amount_n || 0), 0);
              const unlockedN = rows.reduce((s, c) => s + (c.unlocked_n || 0), 0);
              const pct = totalN > 0 ? Math.min(100, Math.round((unlockedN / totalN) * 100)) : 0;
              // The read itself failed — say so and offer the retry, rather than
              // spinning skeletons at someone forever.
              if (!cb && cbErr) {
                return (
                  <div style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 9 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>Couldn&rsquo;t load the split</div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                      The commission breakdown for this sale didn&rsquo;t come back. Check your
                      connection and try again.
                    </div>
                    <Button size="small" onClick={() => setCbTick((t) => t + 1)} style={{ alignSelf: 'flex-start' }}>Try again</Button>
                  </div>
                );
              }
              // Wait for the breakdown before deciding what to draw: a sale HQ hasn't
              // converted has no commission rows at all, and rendering the normal view
              // for it showed "MY COMMISSION RM 0.00" over a full-Net-GCI balance —
              // which reads as money gone missing rather than money not yet split.
              if (!cb) return <Skeletons n={3} height={70} />;
              if (rows.length === 0) {
                return (
                  <div style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {/* Three different things look the same from here — a sale with
                        no split yet, a sale whose split names no line for this
                        agent, and one whose estimate HQ couldn't be asked for.
                        Saying "it's worked out at invoice time" during an outage is
                        a plausible, wrong answer that sends the agent away thinking
                        the app is fine. */}
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                      {cb.preview_unavailable
                        ? 'Estimate unavailable right now'
                        : cb.sale_has_lines ? 'No commission for you on this sale' : 'Commissions aren’t calculated yet'}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                      {cb.preview_unavailable
                        ? 'The projected split couldn’t be worked out just now — nothing is wrong with the sale itself. Try again in a moment.'
                        : cb.sale_has_lines
                          ? 'This sale has been split, but none of it is allocated to you. Speak to HQ if you expected a share.'
                          : 'The split is worked out when HQ raises the invoice for this sale. Until then there’s nothing to unlock or pay out.'}
                    </div>
                    {cb.preview_unavailable && (
                      <Button size="small" onClick={() => setCbTick((t) => t + 1)} style={{ alignSelf: 'flex-start' }}>Try again</Button>
                    )}
                  </div>
                );
              }
              // Projected by HQ from the sale as it stands — no commission rows
              // committed yet. Nothing is owed and the numbers still move, so the
              // knockoff progress bar would be meaningless here.
              //
              // A line HQ keyed by hand is committed from the moment it is added,
              // so a Pending sale can carry both: projected rows (is_preview) AND a
              // real manual row. The estimate wording then belongs to the projected
              // part only — calling the whole card "nothing is owed" would disown a
              // line HQ has already set. The estimate is read off the rows rather
              // than cb.is_preview alone, so a sale whose only line for this agent
              // is a manual one reads as the committed money it is.
              const projRows = rows.filter((c) => c.is_preview);
              const setRows = rows.filter((c) => !c.is_preview);
              const isEstimate = !!cb.is_preview && projRows.length > 0;
              const mixed = isEstimate && setRows.length > 0;
              const heroN = mixed ? projRows.reduce((s, c) => s + (c.amount_n || 0), 0) : totalN;
              const setN = setRows.reduce((s, c) => s + (c.amount_n || 0), 0);
              return (
                <>
                  {isEstimate && (
                    <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--muted)' }}>ESTIMATE &middot; NOT FINAL</div>
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                        {mixed ? (
                          <>
                            The lines marked estimate are worked out from the sale as it stands
                            right now, so they change if the price or the agent split changes.
                            They become final when HQ raises the invoice &mdash; nothing on them
                            is owed or payable until then. Lines HQ added by hand are already set.
                          </>
                        ) : (
                          <>
                            Worked out from the sale as it stands right now, so it changes if the
                            price or the agent split changes. It becomes final when HQ raises the
                            invoice &mdash; nothing is owed or payable until then.
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Personal-commission gradient hero */}
                  <div style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 14, padding: '15px 16px', color: '#fff', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', opacity: 0.72 }}>
                      {isEstimate ? 'MY COMMISSION (ESTIMATE)' : 'MY COMMISSION'}{me?.gci_tier ? ` · ${Math.round(Number(me.gci_tier))}% TIER` : ''}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{RM(heroN)}</div>
                    {!isEstimate && (
                      <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden', marginTop: 5 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#5AA1EA' }} />
                      </div>
                    )}
                    <div style={{ fontSize: 11.5, opacity: 0.8 }}>
                      {isEstimate ? 'Confirmed when HQ raises the invoice' : `Knockoff ${pct}% · unlocks as receipts are validated`}
                    </div>
                    {sale?.commission_hand_set && (
                      <div style={{ fontSize: 11.5, opacity: 0.8 }}>Commission set by HQ finance</div>
                    )}
                    {/* The set lines are kept out of the estimate figure above and
                        stated on their own, so neither number borrows the other's
                        certainty. */}
                    {mixed && (
                      <div style={{ fontSize: 11.5, marginTop: 4, paddingTop: 7, borderTop: '1px solid rgba(255,255,255,.16)', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ opacity: 0.8 }}>Plus added by HQ &middot; already set</span>
                        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM(setN)}</span>
                      </div>
                    )}
                  </div>
                  {rows.map((c) => {
                    const rp = c.amount_n > 0 ? Math.min(100, Math.round((c.unlocked_n / c.amount_n) * 100)) : 0;
                    return (
                      <div key={c.id} style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                            {/* Same type colour the breakdown chips carry, so a line is
                                recognisable in both halves of the tab. */}
                            <span style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: commissionColor(c.commission_type) }} />
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{c.kind}</span>
                              {/* A manual line's `kind` is HQ's own description, which
                                  says what the money is for but not where it came
                                  from — so the origin is named under it. */}
                              {c.is_manual && (
                                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)' }}>{commissionTypeLabel(c.commission_type)}</span>
                              )}
                            </span>
                          </span>
                          {/* Only marked per row when the tab mixes the two; a wholly
                              projected tab already says so in its banner and hero. */}
                          {mixed && c.is_preview ? <Chip label="Estimate" tone="muted" /> : <Chip label={c.chip} />}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontVariantNumeric: 'tabular-nums' }}>
                          <CommStat label="TOTAL" value={c.amount} />
                          <CommStat label="UNLOCKED" value={c.unlocked} tone="var(--accent)" />
                          <CommStat label="PAID" value={c.paid} tone="var(--ok)" />
                        </div>
                        <div style={{ height: 5, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                          <div style={{ width: `${rp}%`, height: '100%', background: 'var(--accent)', borderRadius: 999 }} />
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
            {seg === 'balance' && (!bt ? (
              <div style={{ padding: '4px 0' }}><Skeletons n={3} height={64} /></div>
            ) : (() => {
              // Voided is the one hard block on writing (saleBalanceTo.service) — the
              // money lock isn't, because it protects the commission arithmetic and
              // this is neither commission nor arithmetic.
              const writable = sale.status !== 'Voided';
              const entries = bt.entries || [];
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ ...sectionLabel, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* The dot and colour the Overview card gives the Balance To Owner
                          stage — this tab is the record behind that stage.
                          The heading names both kinds rather than borrowing the stage's
                          wording: a deposit going back to the tenant is a refund, not a
                          balance to the owner, and both are entries on this list. */}
                      <span style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: STAGE_COLOR.Balance }} />
                      BALANCE TO / REFUND TO
                    </div>
                    {entries.length > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM(bt.total)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                    Once the commission invoice is settled, what is left in the branch Client
                    Account belongs to somebody — a balance passed to its owner, or a refund
                    going back to whoever paid it in. Say which, who, how much and into which
                    account — it moves no money, HQ finance still raises the refund voucher.
                  </div>
                  {writable ? (
                    <div onClick={() => openBalance(null)} style={addRow} role="button" tabIndex={0}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openBalance(null); } }}>
                      ＋ Add an entry
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>This sale is cancelled.</div>
                  )}
                  {entries.length === 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '6px 0' }}>Nothing recorded on this sale yet.</div>
                  )}
                  {entries.map((b) => (
                    <div key={b.id} style={rowCard}>
                      {/* Same three-tier anatomy as a receipt card: what kind of entry this
                          is, then who and how much, then the muted detail.
                          The payee GROUP ("Landlord / Vendor") used to lead this row, but the
                          role chip beside it already prints the specific word — Landlord, or
                          Vendor — so it was the vaguer of two answers to one question. The
                          kind of entry is the thing nothing else on the card says. */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <Chip
                          label={b.entry_type_label || BALANCE_ENTRY_TYPE_LABEL[b.entry_type] || 'Balance To'}
                          tone={BALANCE_ENTRY_TYPE_TONE[b.entry_type] || 'brand'}
                        />
                        <Chip label={BALANCE_ROLE_LABEL[b.party_role] || b.party_role} tone="muted" />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginTop: 4 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 700, minWidth: 0, wordBreak: 'break-word', lineHeight: 1.35 }}>{b.party_name}</div>
                        <span style={{ fontSize: 15, fontWeight: 700, flex: 'none', fontVariantNumeric: 'tabular-nums' }}>{RM(b.amount)}</span>
                      </div>
                      {/* Finance pays against this line, so it is spelled out in full rather
                          than summarised — a wrong digit here is money in a stranger's account. */}
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5, lineHeight: 1.45, wordBreak: 'break-word' }}>
                        {bankLine(b.bank_name, b.bank_account_no, b.bank_account_holder)}
                      </div>
                      {b.remarks && (
                        <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--muted)', background: 'var(--surface2)', borderRadius: 8, padding: '7px 10px', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                          {b.remarks}
                        </div>
                      )}
                      <MetaLine parts={[balanceAuthor(b), b.created_at ? dayjs(b.created_at).format('DD MMM YYYY') : null]} />
                      {b.can_edit && writable && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                          <Button size="small" onClick={() => openBalance(b)}>Edit</Button>
                          <AsyncButton size="small" danger onClick={() => removeBalance(b)} onError={(e) => message.error(e.message)}>Delete</AsyncButton>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              );
            })())}
            {seg === 'finance' && (!fin ? (
              <div style={{ padding: '4px 0' }}><Skeletons n={3} height={64} /></div>
            ) : (
              <>
                {/* Collection hero — gradient, matches the design mock */}
                {(() => {
                  const invoiced = fin.rollup.invoiced || 0;
                  const pct = invoiced > 0 ? Math.min(100, Math.round(((fin.rollup.received || 0) / invoiced) * 100)) : 0;
                  return (
                    <div style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 16, padding: 18, color: '#fff', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', opacity: 0.72 }}>COLLECTION</span>
                        <span style={{ fontSize: 11.5, opacity: 0.82 }}>{pct}% collected</span>
                      </div>
                      <div style={{ height: 7, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#5AA1EA', borderRadius: 999 }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px', fontVariantNumeric: 'tabular-nums' }}>
                        <HeroStat label="INVOICED" value={RM(fin.rollup.invoiced)} />
                        <HeroStat label="RECEIVED" value={RM(fin.rollup.received)} />
                        <HeroStat label="CLIENT A/C HELD" value={RM(fin.client_ac.balance)} big />
                        <HeroStat label="BALANCE DUE" value={RM(fin.rollup.invoice_outstanding)} />
                      </div>
                      <div style={{ fontSize: 11.5, opacity: 0.75 }}>Held funds unlock your commission as HQ cuts payment</div>
                    </div>
                  );
                })()}
                {(fin.client_ac.breakdown || []).length > 0 && (
                  <Card>
                    {fin.client_ac.breakdown.map((b, i, arr) => (
                      <div key={b.receipt_id} style={{ padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{b.fee_label}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>{RM(b.remaining_held)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
                          <span>{b.payer}{b.receipt_date ? ` · ${dayjs(b.receipt_date).format('DD MMM YYYY')}` : ''}</span><span>of {RM(b.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </Card>
                )}

                {/* My commission rollup */}
                <div style={sectionLabel}>MY COMMISSION</div>
                <Card>
                  <KV k="Entitled" v={RM(fin.rollup.my.entitled)} />
                  <KV k="Unlocked" v={RM(fin.rollup.my.unlocked)} />
                  <KV k="Paid" v={RM(fin.rollup.my.paid)} />
                </Card>

                {/* Invoices */}
                <div style={sectionLabel}>INVOICES</div>
                {(fin.invoices || []).length === 0 ? (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '6px 0' }}>No invoice raised yet.</div>
                ) : fin.invoices.map((iv) => (
                  <div key={iv.id} style={rowCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{iv.counterparty}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{iv.doc_no || 'Draft'}{iv.counterparty_type ? ` · ${iv.counterparty_type}` : ''}</div>
                      </div>
                      <Chip label={iv.status_label} tone={INVOICE_TONE[iv.status]} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
                      <span>Total <b style={{ color: 'var(--text)' }}>{RM(iv.total)}</b></span>
                      <span>Outstanding <b style={{ color: iv.outstanding > 0 ? 'var(--warn)' : 'var(--text)' }}>{RM(iv.outstanding)}</b></span>
                    </div>
                    {iv.has_pdf && (
                      <div style={{ marginTop: 10 }}>
                        <AsyncButton size="small" onClick={() => downloadInvoicePdf(iv)} onError={(e) => message.error(e.message || 'Could not download the invoice PDF')}>Download PDF</AsyncButton>
                      </div>
                    )}
                    <InvoicePayments payments={iv.payments} onView={viewPaymentPdf} onError={(e) => message.error(e.message || 'Could not open the receipt')} />
                  </div>
                ))}

                {/* Refunds — only if any exist */}
                {(fin.refunds || []).length > 0 && (
                  <>
                    <div style={sectionLabel}>REFUNDS</div>
                    {fin.refunds.map((rv) => (
                      <div key={rv.id} style={rowCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rv.to_name}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{rv.voucher_no || 'Draft'}{rv.to_type ? ` · ${rv.to_type}` : ''}{rv.date ? ` · ${rv.date}` : ''}</div>
                          </div>
                          <Chip label={rv.status_label} tone={REFUND_TONE[rv.status]} />
                        </div>
                        <div style={{ fontSize: 13, marginTop: 6 }}>{RM(rv.amount)}{rv.reason ? ` · ${rv.reason}` : ''}</div>
                      </div>
                    ))}
                  </>
                )}

                {/* Cash movements — only if any exist */}
                {(fin.cash_movements || []).length > 0 && (
                  <>
                    <div style={sectionLabel}>CASH MOVEMENTS</div>
                    {fin.cash_movements.map((m) => (
                      <div key={m.id} style={rowCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.from} → {m.to}</div>
                          <span style={{ fontSize: 13, fontWeight: 700, flex: 'none', fontVariantNumeric: 'tabular-nums' }}>{RM(m.amount)}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{m.purpose}{m.party ? ` · ${m.party}` : ''}{m.or_no ? ` · OR ${m.or_no}` : ''}</div>
                      </div>
                    ))}
                  </>
                )}

                <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>HQ-ONLY · READ-ONLY</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Issuing invoices, validating receipts and cutting refunds are handled by HQ finance. Invoice PDFs are available from HQ once issued.</div>
                </div>
              </>
            ))}
            {seg === 'receipts' && (
              <>
                {/* Uploading is a document action and is open from day one — see
                    the agent server's recordReceipt. It is HQ's *approval* that
                    waits for the money lock, so say so rather than blocking. */}
                {(sale.lock?.can_add_receipt ?? sale.status !== 'Voided') ? (
                  <>
                    <div data-tour="receipt-add" onClick={() => setRcOpen(true)} style={addRow} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRcOpen(true); } }}>
                      ＋ Record a payment receipt
                    </div>
                    {!(sale.lock?.locked ?? (sale.status === 'Converted' || sale.status === 'Completed')) && (
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, padding: '2px 2px 0' }}>
                        Upload it now — HQ finance validates it once the sale has an invoice.
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>This sale is cancelled.</div>
                )}
                {(sale.receipts || []).length === 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '6px 0' }}>No receipts recorded yet.</div>
                )}
                {(sale.receipts || []).map((r, i) => (
                  <div key={r.id} data-tour={i === 0 ? 'receipt-row' : undefined} style={rowCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
                      <Chip label={r.chip} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{r.amount}</div>
                    {/* Received-on date and payer — the two facts the admin table
                        leads with, and what the agent needs to match a slip to a payment. */}
                    <MetaLine parts={[
                      r.receipt_date ? `Received ${dayjs(r.receipt_date).format('DD MMM YYYY')}` : null,
                      r.payer,
                    ]} />
                    {r.notes && (
                      <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--muted)', background: 'var(--surface2)', borderRadius: 8, padding: '7px 10px', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                        {r.notes}
                      </div>
                    )}
                    {/* Bank-in slips backing this receipt — viewable in-app, same
                        signed-URL viewer the Attachments tab uses. */}
                    {(r.attachments || []).length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {r.attachments.map((a) => (
                          <Button
                            key={a.id}
                            size="small"
                            disabled={!a.signed_url}
                            onClick={() => setFileView({ name: a.file_name, url: a.signed_url })}
                            style={{ fontSize: 11.5, maxWidth: '100%' }}
                          >
                            <span style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              📎 {a.file_name}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}
                    {/* finance's verdict — why it was rejected, so the agent can fix + re-record */}
                    {r.rejection_reason && (
                      <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--bad)', background: 'color-mix(in srgb,var(--bad) 8%,transparent)', border: '1px solid color-mix(in srgb,var(--bad) 25%,var(--border))', borderRadius: 8, padding: '7px 10px', lineHeight: 1.45 }}>
                        <b>Rejected by finance:</b> {r.rejection_reason}
                      </div>
                    )}
                    {r.validated_at && (
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5 }}>
                        {r.chip === 'Rejected' ? 'Rejected' : 'Validated'} on {new Date(r.validated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {r.removable && (
                      <div style={{ marginTop: 8 }}>
                        <AsyncButton size="small" danger onClick={() => removeReceipt(r)} onError={(e) => message.error(e.message)}>Remove</AsyncButton>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {seg === 'invoices' && (
              !fin ? <Skeletons n={2} height={80} /> : (
                <>
                  {(fin.invoices || []).length === 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '10px 0' }}>No invoices for this sale yet.</div>
                  )}
                  {(fin.invoices || []).map((inv, i) => (
                    <div key={inv.id} data-tour={i === 0 ? 'invoice-row' : undefined} style={rowCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{inv.doc_no || 'DRAFT'}</div>
                        <Chip label={inv.status_label || inv.status} />
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>{inv.counterparty}{inv.counterparty_type ? ` · ${inv.counterparty_type}` : ''}</div>
                      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11.5 }}>
                        <span>Subtotal <b>{RM(inv.subtotal)}</b></span>
                        <span>SST <b>{RM(inv.sst_amount)}</b></span>
                        <span>Total <b>{RM(inv.total)}</b></span>
                      </div>
                      <div style={{ fontSize: 11.5, marginTop: 3 }}>Outstanding <b style={{ color: Number(inv.outstanding) > 0 ? 'var(--warn)' : 'var(--ok)' }}>{RM(inv.outstanding)}</b></div>
                      {/* An invoice recorded manually is issued but may have no
                          scan attached yet, so the button follows the document
                          rather than the status — otherwise it would 404 on tap. */}
                      {inv.has_pdf ? (
                        <div style={{ marginTop: 9 }}>
                          <AsyncButton data-tour="invoice-view-pdf" block onClick={() => viewInvoicePdf(inv)} onError={(e) => message.error(e.message)}>View PDF · Share / Print</AsyncButton>
                        </div>
                      ) : (
                        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 7 }}>
                          {inv.issued
                            ? 'No document has been uploaded for this invoice yet.'
                            : 'PDF available once HQ issues this invoice.'}
                        </div>
                      )}
                      {/* What has actually been paid against it, and the customer's
                          receipt for each — the invoice on its own never showed it. */}
                      <InvoicePayments payments={inv.payments} onView={viewPaymentPdf} onError={(e) => message.error(e.message || 'Could not open the receipt')} />
                    </div>
                  ))}
                  <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', fontSize: 12.5, color: 'var(--muted)' }}>
                    Invoices are created and issued by HQ finance — this view is read-only.
                  </div>
                </>
              )
            )}
            {seg === 'attachments' && (
              <>
                {/* Soft (non-blocking) required-documents checklist — admin parity.
                    The co-share form joins it only on a multi-agent sale: a single
                    REN has no fee to share, so listing it there would ask for a
                    document that can never apply. It leaves the list again by
                    being issued, which is what files it against the sale. */}
                {(() => {
                  const required = csf?.applicable
                    ? [{ categories: ['INTERNAL_CO_SHARE_FORM'], label: 'Internal co-share form' }, ...REQUIRED_DOCS]
                    : REQUIRED_DOCS;
                  const missing = required.filter((req) => !req.categories.some((c) => atts.some((a) => a.category === c)));
                  if (!missing.length) return null;
                  const labels = missing.map(docLabel);
                  return (
                    <div style={{ background: 'color-mix(in srgb,var(--warn) 9%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 30%,var(--border))', borderRadius: 12, padding: '14px 15px' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.05em', color: 'var(--warn)' }}>DOCUMENTS STILL EXPECTED</div>
                      <div style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>{labels.join(' · ')}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>Advisory only — HQ expects these on a claim submission.</div>
                    </div>
                  );
                })()}
                {/* Uploader — the type picker lives in a sheet; picking a type opens
                    the camera straight away, so adding a document is one tap not three. */}
                <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={handleFile} style={{ display: 'none' }} />
                <div onClick={() => !uploading && setCatOpen(true)} style={{ ...addRow, opacity: uploading ? 0.55 : 1 }} role="button" tabIndex={0}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !uploading) { e.preventDefault(); setCatOpen(true); } }}>
                  {uploading ? 'Uploading…' : '＋ Add a document'}
                </div>

                {/* List. The co-share form leads it: it is a document on this sale
                    like any other, and the only one you make rather than upload —
                    so it takes the same row and differs only in its action. */}
                {csf?.applicable && (
                  <CoShareFormRow
                    csf={csf}
                    onOpen={(fid) => nav(`/co-share-forms/${fid}`)}
                    onRaise={raiseCoShareForm}
                    onError={(e) => message.error(e.message)}
                  />
                )}
                {uploadedAtts.length === 0 && !uploading && (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '10px 0' }}>
                    {csf?.applicable ? 'Nothing uploaded yet.' : 'No documents uploaded yet.'}
                  </div>
                )}
                {uploadedAtts.map((a) => {
                  // An ATP_ATR row is not a file — it is the join to the booking form
                  // this sale came from. Removing it unlinks the form (freeing it to be
                  // converted again); it never deletes the form or any document. Calling
                  // that "Delete" invited the agent to think the form was going with it.
                  const isFormLink = !!a.booking_form_id;
                  return (
                    <div key={a.id} style={rowCard}>
                      {/* Same three-tier anatomy as a receipt card: what kind of thing
                          it is + its state, then the thing itself, then muted context. */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{a.category_label || 'Document'}</div>
                        {/* This slot is the document's standing with the sale: once a
                            slip backs a payment receipt it is that receipt's proof and
                            can no longer be removed, which is why Delete disappears. */}
                        {a.linked_to_receipt && <Chip label={a.linked_label || "On a receipt"} tone="warn" />}
                        {isFormLink && !a.linked_to_receipt && <Chip label="Linked form" tone="brand" />}
                      </div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 4, wordBreak: 'break-word', lineHeight: 1.35 }}>
                        {a.booking_form?.form_ref || a.file_name}
                        {a.is_verified && <VerifiedMark />}
                      </div>
                      <MetaLine parts={[a.when, fmtSize(a.file_size), a.uploaded_by_type === 'ADMIN' ? 'by HQ' : null]} />
                      <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center' }}>
                        {a.signed_url && (
                          <Button size="small" onClick={() => setFileView({ name: a.file_name, url: a.signed_url })}>View</Button>
                        )}
                        {isFormLink && (
                          <Button size="small" onClick={() => nav(`/forms/${a.booking_form_id}`)}>Open form</Button>
                        )}
                        {!a.linked_to_receipt && (
                          <Button size="small" danger onClick={() => setRemoveTarget(a)}>
                            {isFormLink ? 'Unlink' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

        </>
      )}

      {/* Record-a-receipt sheet — the form used to sit above the list; it now opens
          from the dashed row so the Receipts tab leads with the receipts. */}
      <FormSheet
        open={rcOpen}
        title="Record a receipt"
        sub={`${sale?.ref || ''} · goes to HQ finance for validation`}
        onClose={closeRc}
        footer={(
          <>
            <Button size="large" style={{ height: 46 }} onClick={closeRc}>Cancel</Button>
            <AsyncButton data-tour="receipt-submit" type="primary" block onClick={recordReceipt} onError={(e) => message.error(e.message)}>Record receipt</AsyncButton>
          </>
        )}
      >
        <RField req label="Fee type"><SheetSelect title="Fee type" value={rc.fee_type} onChange={(v) => setRcv({ fee_type: v })} options={FEE_TYPE_OPTIONS} /></RField>
        {rc.fee_type === 'OTHER' && <RField req label="Describe fee"><Input value={rc.fee_type_other} onChange={(e) => setRcv({ fee_type_other: e.target.value })} placeholder="Fee description" /></RField>}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}><RField req label="Amount (RM)"><Input data-tour="receipt-amount" type="number" min={0} value={rc.amount} onChange={(e) => setRcv({ amount: e.target.value })} placeholder="0.00" /></RField></div>
          <div style={{ flex: 1 }}>
            <RField req label="Receipt date">
              {/* Money can't be received in the future — the slip is already in hand. */}
              <SheetDate title="Receipt date" placeholder="Date received" value={rc.receipt_date}
                onChange={(d) => setRcv({ receipt_date: d })} allowClear={false} maxDate={dayjs()} />
            </RField>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}><RField label="Payer type"><SheetSelect title="Payer type" value={rc.payer_type} onChange={(v) => setRcv({ payer_type: v })} options={PAYER_TYPE_OPTIONS} allowClear placeholder="Select" /></RField></div>
          <div style={{ flex: 1 }}><RField label="Payer name"><Input value={rc.payer_name} onChange={(e) => setRcv({ payer_name: e.target.value })} placeholder="Name" /></RField></div>
        </div>
        {/* Bank-in slips — link already-uploaded ones AND/OR upload a new one (admin parity) */}
        {bankSlips.length > 0 && (
          <RField label="Link existing bank-in slip(s)">
            <SheetSelect
              tour="receipt-link-slip"
              title="Link bank-in slips"
              mode="multiple"
              value={rc.attachment_ids}
              onChange={(v) => setRcv({ attachment_ids: v })}
              options={bankSlips}
              placeholder="Link uploaded slip(s)"
              allowClear
              notFoundContent="No bank-in slips uploaded on this sale yet."
            />
          </RField>
        )}
        <input ref={slipRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={handleSlipFile} style={{ display: 'none' }} />
        <Button block loading={slipUploading} onClick={() => slipRef.current?.click()} style={{ border: '1.5px dashed var(--border)', background: 'transparent', color: 'var(--muted)', fontWeight: 600, fontSize: 13 }}>
          {slipUploading ? 'Uploading…' : '↑ Upload new bank-in slip'}
        </Button>
        <RField label="Notes (optional)"><Input.TextArea value={rc.notes} onChange={(e) => setRcv({ notes: e.target.value })} rows={3} placeholder="Anything HQ finance should know" /></RField>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
          HQ finance reviews this before it counts toward the sale's collection.
        </div>
      </FormSheet>

      {/* Balance / refund sheet — six steps in the order they are answered: which
          kind of entry this is, what kind of payee, which one, how much, into which
          account, and anything finance should know. Each step clears the ones below
          it, because an account belongs to a party and a party belongs to a group —
          the kind is the exception, since a balance and a refund are paid to the same
          people out of the same accounts. A lawyer is the other exception: no contact
          stands behind one, so the name is typed and the account comes with it. */}
      <FormSheet
        open={btOpen}
        title={btEdit ? 'Edit this entry' : 'Add an entry'}
        sub={`${sale?.ref || ''} · tells HQ finance who to pay — it moves no money`}
        onClose={closeBt}
        footer={(
          <>
            <Button size="large" style={{ height: 46 }} onClick={closeBt}>Cancel</Button>
            <AsyncButton type="primary" block onClick={saveBalance} onError={(e) => message.error(e.message)}>
              {btEdit ? 'Save changes' : 'Add entry'}
            </AsyncButton>
          </>
        )}
      >
        {/* Asked first because it changes what the next question means, and because
            a refund mis-filed as a balance reads to HQ as a settlement. Nothing below
            depends on it, so changing it clears nothing. */}
        <RField req label="Type">
          <SheetSelect
            title="Type"
            placeholder="Select"
            value={bf.entry_type}
            onChange={(v) => setBfv({ entry_type: v })}
            options={Object.keys(BALANCE_ENTRY_TYPE_LABEL).map((v) => ({
              value: v,
              label: BALANCE_ENTRY_TYPE_LABEL[v],
              sub: BALANCE_ENTRY_TYPE_SUB[v],
            }))}
          />
        </RField>

        <RField req label={BALANCE_PAYEE_LABEL[bf.entry_type] || 'Balance to'}>
          <SheetSelect
            title={BALANCE_PAYEE_LABEL[bf.entry_type] || 'Balance to'}
            placeholder="Select"
            value={bf.party_type}
            selectedLabel={btEdit && bf.party_type === btEdit.party_type ? btEdit.party_type_label : undefined}
            onChange={(v) => setBfv({ party_type: v, party_id: undefined, party_name: '', bank_account_id: undefined, bank_name: '', account_no: '', account_holder_name: '' })}
            options={(btPayees || []).map((g) => ({
              value: g.party_type,
              label: g.label,
              // A free-text group has nobody to list and nobody to be missing, so
              // "nobody on this sale" would be true of it and still be the wrong thing
              // to say — it reads as a group that can't be used yet.
              sub: g.free_text
                ? 'not a party on the deal — typed in'
                : (g.parties.length === 0 ? 'nobody on this sale' : g.parties.map((p) => p.name).join(', ')),
            }))}
            notFoundContent="Couldn’t read the parties on this sale."
          />
        </RField>

        <RField req label="Pay to">
          {/* A lawyer is typed, not picked, and gets no empty-group line either: that
              group is empty because lawyers are typed in, not because this sale is
              missing one.
              For the other three, only claim the group is empty once the payees have
              actually been read — a failed read is not "nobody is on this sale", and it
              is the answer that sends the agent to the Parties tab to fix nothing. */}
          {btFreeText ? (
            <Input value={bf.party_name} onChange={(e) => setBfv({ party_name: e.target.value })}
              placeholder="Law firm or solicitor — e.g. Tan & Partners" />
          ) : btPayees && bf.party_type && (btGroup?.parties || []).length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{BALANCE_EMPTY_HINT[bf.party_type]}</div>
          ) : (
            <SheetSelect
              title="Pay to"
              placeholder={bf.party_type ? 'Select' : `Pick “${BALANCE_PAYEE_LABEL[bf.entry_type] || 'Balance to'}” first`}
              disabled={!bf.party_type}
              value={bf.party_id}
              // A payee taken off the sale after the entry was raised is no longer in
              // the picker; the entry's own snapshot keeps the field readable.
              selectedLabel={btEdit && bf.party_id === btEdit.party_id ? btEdit.party_name : undefined}
              onChange={(v) => setBfv({ party_id: v, bank_account_id: undefined, bank_name: '', account_no: '', account_holder_name: '' })}
              options={(btGroup?.parties || []).map((p) => ({
                value: p.id,
                label: p.name,
                sub: [BALANCE_ROLE_LABEL[p.role] || p.role, p.ren_no ? `REN ${p.ren_no}` : null].filter(Boolean).join(' · '),
              }))}
            />
          )}
          {/* One option under Agent is the rule, not a short list: a co-agent's account
              number is theirs, so a balance owed to anyone else is raised by HQ. Said
              here, where the agent would otherwise go looking for the missing names. */}
          {bf.party_type === 'AGENT' && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 6 }}>
              Only you can be named here — HQ records a {balanceNoun(bf.entry_type)} owed to another agent on this sale.
            </div>
          )}
        </RField>

        <RField req label="Amount (RM)">
          <MoneyInput value={bf.amount} onChange={(v) => setBfv({ amount: v })} placeholder="0.00" />
        </RField>

        <RField req label="Bank account">
          {/* Nothing on file → straight to the fields that create one. Offering an empty
              picker first would be a dead end with an "add" row hidden inside it. A
              lawyer has nothing on file by definition, so it is never offered one. */}
          {btFreeText ? null : !btParty ? (
            <SheetSelect title="Bank account" placeholder="Pick who you’re paying first" disabled value={undefined} options={[]} />
          ) : btBanks.length > 0 ? (
            <SheetSelect
              title="Bank account"
              placeholder="Select an account"
              value={bf.bank_account_id}
              selectedLabel={btSelectedBank}
              onChange={(v) => setBfv({ bank_account_id: v, bank_name: '', account_no: '', account_holder_name: '' })}
              options={[
                ...btBanks.map((acc) => ({
                  value: acc.id,
                  label: bankLine(acc.bank_name, acc.account_no, acc.account_holder_name),
                  sub: acc.is_primary ? 'Primary account' : undefined,
                })),
                { value: NEW_BANK, label: '＋ Add a new bank account' },
              ]}
            />
          ) : null}
          {btNewBank && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: btBanks.length > 0 ? 10 : 0 }}>
              <Input value={bf.bank_name} onChange={(e) => setBfv({ bank_name: e.target.value })} placeholder="Bank name — e.g. Maybank" />
              <Input value={bf.account_no} onChange={(e) => setBfv({ account_no: e.target.value })} placeholder="Account number" inputMode="numeric" />
              <Input value={bf.account_holder_name} onChange={(e) => setBfv({ account_holder_name: e.target.value })} placeholder={`Account holder — defaults to ${btPayeeName || 'the payee'}`} />
              <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                {btFreeText
                  ? 'A solicitor isn’t a contact on this deal, so the name and account you type are held on this entry alone — nothing is filed against a contact record.'
                  : btBanks.length === 0
                    ? `${btParty?.name || 'This party'} has no bank account on file. This one is saved against them, so the next deal can just pick it.`
                    : 'Saved against this party, so the next deal can just pick it.'}
              </div>
            </div>
          )}
        </RField>

        <RField label="Remarks (optional)">
          <Input.TextArea value={bf.remarks} onChange={(e) => setBfv({ remarks: e.target.value })} rows={3} maxLength={500}
            placeholder={bf.entry_type === 'REFUND' ? 'e.g. Earnest deposit returned — deal aborted' : 'e.g. Balance of rental deposit after commission'} />
        </RField>
      </FormSheet>

      {/* Removing an attachment, in its two forms.
          Unlink says what SURVIVES, because the row is labelled like a document and an
          agent could reasonably fear the form goes with it (the admin makes the same
          promise: "the booking form itself will not be deleted"). Delete says what does
          NOT survive, because it is the irreversible one. */}
      {(() => {
        const a = removeTarget;
        const isFormLink = !!a?.booking_form_id;
        return (
          <Modal
            open={!!a}
            title={isFormLink ? 'Unlink this booking form?' : 'Delete this document?'}
            footer={null}
            onCancel={() => setRemoveTarget(null)}
            destroyOnHidden
            centered
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {isFormLink ? (
                <>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                    This removes the link between <b style={{ color: 'var(--text)' }}>{a?.booking_form?.form_ref || 'the form'}</b> and {sale?.ref}.
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
                    <li>The booking form itself is not deleted.</li>
                    <li>It becomes convertible again, so it can be turned into another sale.</li>
                    <li>This sale keeps its figures and parties — nothing else changes.</li>
                  </ul>
                </>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                  <b style={{ color: 'var(--text)' }}>{a?.file_name}</b> is removed from {sale?.ref} and deleted from storage.
                  This cannot be undone — you would have to upload it again.
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                <Button block onClick={() => setRemoveTarget(null)}>{isFormLink ? 'Keep it linked' : 'Keep it'}</Button>
                <AsyncButton block danger onClick={confirmRemove} onError={(e) => message.error(e.message)}>
                  {isFormLink ? 'Unlink' : 'Delete'}
                </AsyncButton>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Which letterhead the co-share form prints on.
          Only ever opens when the answer is genuinely ambiguous — the RENs on this
          sale sit in different branches, so nothing in the record says whose paper
          it is. Every rule that could have inferred one fails on the case that
          matters (most co-shares are split evenly, and most multi-agent sales have
          no creating agent), and a wrong branch on a signed fee agreement only
          surfaces when someone disputes the split. So it asks, once, and the
          answer is frozen onto the form. */}
      <Modal
        open={!!branchAsk}
        title="Which branch&rsquo;s letterhead?"
        footer={null}
        onCancel={() => setBranchAsk(null)}
        destroyOnHidden
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            The RENs on {sale?.ref} are in different branches, so this one is yours to decide.
            It is printed on the form and fixed once you issue it.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(branchAsk?.choices || []).map((b) => {
              const on = b.id === branchPick;
              return (
                <div
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={on}
                  onClick={() => setBranchPick(b.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBranchPick(b.id); } }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 15px', cursor: 'pointer',
                    borderRadius: 12, background: on ? 'var(--chip)' : 'var(--surface)',
                    border: `1.5px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                  }}
                >
                  {/* A filled ring rather than a tick: two branches, one of which is
                      already selected, so the control has to show which. */}
                  <span style={{ flex: 'none', width: 17, height: 17, marginTop: 1, borderRadius: 50, border: `2px solid ${on ? 'var(--primary)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {on && <span style={{ width: 8, height: 8, borderRadius: 50, background: 'var(--primary)' }} />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{b.name}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, marginTop: 1 }}>
                      {b.reason}
                      {b.id === branchAsk?.preferred_branch_id ? ' · suggested' : ''}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
            <Button block onClick={() => setBranchAsk(null)}>Cancel</Button>
            <AsyncButton
              block
              type="primary"
              disabled={!branchPick}
              onClick={() => raiseCoShareForm(branchPick)}
              onError={(e) => message.error(e.message)}
            >
              Generate form
            </AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Cancel (void) — the admin void modal, phrased for the agent.
          A sale converted from a booking form leaves that form behind, and what
          should happen to it is a decision only the agent can make: cancel it too,
          or keep it and convert it again later. */}
      <Modal open={cxOpen} title="Cancel this sale?" footer={null} onCancel={() => setCxOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            <b style={{ color: 'var(--text)' }}>{sale?.ref}</b> stays on record, marked Voided.
            You can&rsquo;t undo this yourself — once HQ converts a sale, only they can void it.
          </div>

          {linkedForm ? (
            <div style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                This sale was created from booking form <b>{linkedForm.ref}</b>. Keep it and it
                stays issued, holds the listing Under Offer, and unlinks from this sale so you
                can convert it again.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Also cancel {linkedForm.ref}</span>
                <Switch checked={cxForm} onChange={setCxForm} />
              </div>
              {cxForm && (
                <RField req label="Cancellation reason">
                  <Input.TextArea value={cxReason} onChange={(e) => setCxReason(e.target.value)} rows={3} maxLength={1000}
                    placeholder="e.g. Buyer pulled out; financing fell through" />
                </RField>
              )}
            </div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
              <li>Any linked listing goes back to <strong style={{ color: 'var(--text)' }}>Active</strong>.</li>
              <li>Any reserved project unit is released.</li>
              <li>Receipts already uploaded stay as they are — HQ finance still deals with them.</li>
            </ul>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={() => setCxOpen(false)}>Keep it</Button>
            <AsyncButton block danger disabled={cxForm && !cxReason.trim()} onClick={cancelSale} onError={(e) => message.error(e.message)}>
              {cxForm ? 'Cancel both' : 'Cancel sale'}
            </AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Delete — the reference has to be typed back, exactly as in the admin
          portal. Nothing survives this one, so a single tap must not be enough. */}
      <Modal open={delOpen} title="Delete this sale?" footer={null} onCancel={() => setDelOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            This removes <b style={{ color: 'var(--text)' }}>{sale?.ref}</b> and everything on it —
            parties, splits, documents. Nothing is kept and it cannot be recovered. If the deal
            simply fell through, <b style={{ color: 'var(--text)' }}>cancel</b> it instead: that keeps the record.
          </div>

          {linkedForm && (
            <div style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                This sale was created from booking form <b>{linkedForm.ref}</b>. Keep it and it stays
                issued, holds the listing Under Offer, and can be converted again.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Also cancel {linkedForm.ref}</span>
                <Switch checked={delForm} onChange={setDelForm} />
              </div>
              {delForm && (
                <RField req label="Cancellation reason">
                  <Input.TextArea value={delReason} onChange={(e) => setDelReason(e.target.value)} rows={3} maxLength={1000}
                    placeholder="e.g. Buyer pulled out; financing fell through" />
                </RField>
              )}
            </div>
          )}

          <RField req label={`Type ${sale?.ref} to confirm`}>
            <Input value={delRef} onChange={(e) => setDelRef(e.target.value)} placeholder={sale?.ref} autoCapitalize="characters" autoCorrect="off" />
          </RField>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={() => setDelOpen(false)}>Keep it</Button>
            <AsyncButton block danger disabled={!delRefOk || (delForm && !delReason.trim())} onClick={discard} onError={(e) => message.error(e.message)}>
              Delete permanently
            </AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Document-type picker — a grouped list, not a grid.
          Fourteen equal cells in two columns give the eye nothing to hold on to and
          run in the enum's order, which is nobody's mental model. Grouped by where a
          document sits in the life of a deal, you scan four headings instead of
          fourteen labels — and the search box is there for whoever would rather type.
          It does NOT take focus on open: this is a list you browse, and the keyboard
          would cover it.

          Picking one opens the camera in the same tap, so uploading stays one tap. */}
      <OptionSheet
        open={catOpen}
        title="What is this document?"
        onClose={() => setCatOpen(false)}
        searchable
        autoFocusSearch={false}
        searchPlaceholder="Search document types…"
        q={catQ}
        setQ={setCatQ}
      >
        <div style={{ padding: '4px 12px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(() => {
            const term = catQ.trim().toLowerCase();
            const shown = term
              ? CATEGORY_OPTIONS.filter((o) => o.label.toLowerCase().includes(term))
              : CATEGORY_OPTIONS;
            if (!shown.length) {
              return <div style={{ padding: '26px 8px', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>No document type matches “{catQ.trim()}”.</div>;
            }
            const pick = (value) => {
              // Open the picker inside this same tap: Safari refuses a programmatic
              // file-input click once the user gesture has ended, so no setTimeout
              // here. The category rides on a ref rather than state so handleFile
              // can't read a stale value.
              pendingCat.current = value;
              fileRef.current?.click();
              setCatOpen(false);
              setCatQ('');
            };
            return CATEGORY_GROUPS.map((g) => {
              const inGroup = shown.filter((o) => o.group === g.key);
              if (!inGroup.length) return null;
              return (
                <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>{g.label}</div>
                  {inGroup.map((o) => {
                    const attached = atts.filter((a) => a.category === o.value).length;
                    return (
                      <button
                        key={o.value}
                        onClick={() => pick(o.value)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                          border: '1px solid var(--border)', borderRadius: 11, padding: '14px 15px',
                          background: 'var(--surface)', cursor: 'pointer', textAlign: 'left',
                          font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3,
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
                        {attached > 0 && (
                          <span style={{ flex: 'none', fontSize: 11.5, fontWeight: 600, color: 'var(--ok)' }}>
                            ✓ {attached > 1 ? `${attached} added` : 'added'}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            });
          })()}
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 10 }}>
            PDF or photo, up to 10 MB. You can add more than one of the same type.
          </div>
        </div>
      </OptionSheet>

      {/* Notes FAB — on every tab, like the admin workspace comments button.
          Talking to HQ is just as likely while looking at receipts or documents
          as on the overview, so it shouldn't be something you navigate back to. */}
      {sale && !notesOpen && (
        <div onClick={() => setNotesOpen(true)} style={{ position: 'fixed', right: 18, bottom: 'calc(20px + env(safe-area-inset-bottom))', width: 52, height: 52, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 8px 20px color-mix(in srgb,var(--primary) 45%,transparent)', zIndex: 40 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3 8.9 8.9 0 0 1-3.2-.6L3 21l1.9-5.6a8 8 0 0 1-.9-3.9A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.5 8.5Z" /></svg>
          {noteUnread > 0 && (
            <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 19, height: 19, borderRadius: 999, background: 'var(--bad)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: '2px solid var(--bg)' }}>
              {noteUnread > 99 ? '99+' : noteUnread}
            </span>
          )}
        </div>
      )}

      {/* Notes sheet — the admin comment drawer, re-laid-out for a phone:
          rich text, threaded replies, reactions, @mentions, /resource links and
          per-comment attachments all live in SaleNotesSheet. */}
      {sale && (
        <SaleNotesSheet
          saleId={id}
          saleRef={sale.ref}
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          onReadAll={() => setNoteUnread(0)}
          onResourceClick={openResource}
          onOpenFile={setFileView}
        />
      )}

      {/* In-app document viewer (popup-safe on mobile) */}
      {/* Change stage. Lists ONLY what this agent may set, flat and unordered, with
          the collapsed-deal report kept separate from the four asks. HQ's own stages
          never appear here — they were never the agent's to pick. */}
      {/* A bottom sheet, not a full-screen page: this is a short pick, and a
          takeover for it reads as a bigger commitment than it is. OptionSheet is the
          app's own partial-height chrome (scrim, grab handle, exit animation) and it
          hugs its content, so the panel is only as tall as the choices. */}
      <OptionSheet open={stageSheet} title="Change stage" onClose={() => setStageSheet(false)}>
        <div style={{ padding: '4px 12px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          Pick whichever applies — these aren&rsquo;t in order.
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' }}>ASK HQ FOR</div>
        {AGENT_REQUEST_STAGES.filter((s) => (sale?.settable_stages || []).includes(s)).map((s) => {
          const cur = s === sale?.stage_raw;
          return (
            <div
              key={s}
              data-tour={`stage-opt-${s}`}
              onClick={cur ? undefined : () => confirmStageChange(s)}
              role="button"
              tabIndex={cur ? -1 : 0}
              onKeyDown={(e) => { if (!cur && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); confirmStageChange(s); } }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 14px', borderRadius: 11,
                border: `${cur ? 1.5 : 1}px solid ${cur ? STAGE_COLOR[AGENT_STAGE_LABEL[s]] || 'var(--primary)' : 'var(--border)'}`,
                background: cur ? `color-mix(in srgb, ${STAGE_COLOR[AGENT_STAGE_LABEL[s]] || '#1890ff'} 8%, var(--surface))` : 'var(--surface)',
                cursor: cur ? 'default' : 'pointer',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', background: cur ? STAGE_COLOR[AGENT_STAGE_LABEL[s]] : 'var(--border)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650 }}>{AGENT_STAGE_LABEL[s]}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.4 }}>{STAGE_BLURB[s]}</div>
              </div>
              {cur && <span style={{ fontSize: 11.5, color: 'var(--muted)', flex: 'none' }}>current</span>}
            </div>
          );
        })}

        {(sale?.settable_stages || []).includes(AGENT_EXCEPTION_STAGE) && (
          <>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--bad)', marginTop: 6 }}>IF THE DEAL FELL THROUGH</div>
            <div
              data-tour={`stage-opt-${AGENT_EXCEPTION_STAGE}`}
              onClick={sale?.stage_raw === AGENT_EXCEPTION_STAGE ? undefined : () => confirmStageChange(AGENT_EXCEPTION_STAGE)}
              role="button"
              tabIndex={sale?.stage_raw === AGENT_EXCEPTION_STAGE ? -1 : 0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); confirmStageChange(AGENT_EXCEPTION_STAGE); } }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 14px', borderRadius: 11,
                border: `1px solid color-mix(in srgb, var(--bad) 40%, var(--border))`,
                background: sale?.stage_raw === AGENT_EXCEPTION_STAGE ? 'color-mix(in srgb, var(--bad) 8%, var(--surface))' : 'var(--surface)',
                cursor: sale?.stage_raw === AGENT_EXCEPTION_STAGE ? 'default' : 'pointer',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', background: 'var(--bad)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--bad)' }}>Cancel / refund / loan reject</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.4 }}>{STAGE_BLURB[AGENT_EXCEPTION_STAGE]}</div>
              </div>
              {sale?.stage_raw === AGENT_EXCEPTION_STAGE && <span style={{ fontSize: 11.5, color: 'var(--muted)', flex: 'none' }}>current</span>}
            </div>
          </>
        )}
        </div>
      </OptionSheet>

      {fileView && <FileViewerLazy title={fileView.name} url={fileView.url} onClose={() => { if (fileView.url.startsWith('blob:')) URL.revokeObjectURL(fileView.url); setFileView(null); }} />}
    </div>
  );
}

// `kv-card` kills the last row's separator — it doubled up with the card's own edge
// and drew a stray line under the final value (see design.css).
const Card = ({ children }) => <div className="kv-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 14px' }}>{children}</div>;
// `req` renders the admin-portal-style red asterisk on mandatory fields.
const RField = ({ label, req, children }) => (
  <div>
    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 5 }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </div>
    {children}
  </div>
);
const rowCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' };
// The muted context line under a card's headline. Receipts and documents share it
// so the two lists read as one family; blank parts drop out with their separator.
const MetaLine = ({ parts }) => {
  const shown = (parts || []).filter(Boolean);
  if (!shown.length) return null;
  return <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{shown.join(' · ')}</div>;
};
// HQ's sign-off on a document. Rides inline after the file name rather than in a
// chip slot: it is a property of the file itself, and the slot up top is reserved
// for the document's standing with the sale. Flows with the text so it lands after
// the last word of a name that wraps, instead of being pushed onto its own line.
const VerifiedMark = () => (
  <span
    role="img"
    aria-label="Verified by HQ"
    title="Verified by HQ"
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6, verticalAlign: '1px',
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', color: 'var(--ok)', whiteSpace: 'nowrap',
    }}
  >
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10" fill="var(--ok)" />
      <path d="M7.4 12.2l3.1 3.1 6.1-6.1" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    VERIFIED
  </span>
);
// A bank account as one line: "MAYBANK · 512345678901 · YUSUF TAIYOOB SDN BHD".
// Takes the three parts rather than a row, because an entry carries the SNAPSHOT it
// was raised against (bank_name / bank_account_no / bank_account_holder) while the
// picker offers the party's live accounts (bank_name / account_no / holder) — and
// the entry must keep showing what finance will actually pay against.
const bankLine = (bank, accountNo, holder) => [bank, accountNo, holder].filter(Boolean).join(' · ');
// Whose entry this is. HQ's are read-only here, so the line has to say so — a card
// with no Edit button and no explanation reads as the app being broken.
const balanceAuthor = (b) => {
  if (b.created_by_type === 'ADMIN') return `Added by HQ${b.created_by_name ? ` · ${b.created_by_name}` : ''}`;
  if (b.can_edit) return 'Added by you';
  return `Added by ${b.created_by_name || 'another agent on this sale'}`;
};
const fmtSize = (bytes) => {
  const b = Number(bytes) || 0;
  if (!b) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
// Inline "add" trigger that sits at the top of a list, where the new row will land.
const addRow = {
  border: '1.5px dashed color-mix(in srgb, var(--primary) 50%, var(--border))',
  borderRadius: 14, padding: '15px 16px', textAlign: 'center', cursor: 'pointer',
  color: 'var(--primary)', fontSize: 13, fontWeight: 600,
  background: 'color-mix(in srgb, var(--primary) 4%, var(--surface))',
};
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 2 };

/**
 * The official receipts raised against one invoice.
 *
 * When HQ applies collected money to an invoice, the company issues the customer an
 * official receipt — and that is the document the customer comes back to the agent
 * for. The app used to show only the outstanding figure falling, with the receipt
 * itself reachable nowhere, so it lives here: nested under the invoice it paid,
 * which is the same place the admin Invoices tab keeps it.
 *
 * A voided one stays listed — money coming back off an invoice is a fact the agent
 * needs — but carries no document, because a voided OR receipts nothing.
 */
const InvoicePayments = ({ payments, onView, onError }) => {
  const rows = payments || [];
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', color: 'var(--muted)' }}>OFFICIAL RECEIPTS</div>
      {rows.map((p) => (
        <div key={p.id} style={{ marginTop: 9 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'ui-monospace,monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: p.voided ? 'line-through' : 'none' }}>
                {p.or_no || 'No receipt no.'}
              </div>
              <MetaLine parts={[
                p.date ? dayjs(p.date).format('DD MMM YYYY') : null,
                p.source === 'MANUAL' ? 'Branch receipt book' : null,
              ]} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
              <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM(p.amount)}</span>
              <Chip label={p.status_label || p.status} tone={PAYMENT_TONE[p.status]} />
            </div>
          </div>
          {p.has_pdf ? (
            <div style={{ marginTop: 7 }}>
              <AsyncButton size="small" block onClick={() => onView(p)} onError={onError}>View receipt · Share / Print</AsyncButton>
            </div>
          ) : (
            !p.voided && (
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5 }}>
                {p.source === 'MANUAL'
                  ? 'HQ has not uploaded this receipt document yet.'
                  : 'Receipt available once this payment reaches accounting.'}
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
};
const HeroStat = ({ label, value, big }) => (
  <div>
    <div style={{ fontSize: 11, opacity: 0.65, fontWeight: 600, letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: big ? 19 : 13, fontWeight: big ? 700 : 600, color: big ? '#9DC6F2' : '#fff' }}>{value}</div>
  </div>
);
const CommStat = ({ label, value, tone }) => (
  <div>
    <div style={{ fontSize: 10.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: tone || 'var(--text)' }}>{value}</div>
  </div>
);
const Muted = ({ children }) => <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{children}</span>;
/** One or more form refs, stacked; linked when the forms screen will open it for this agent. */
const FormRefs = ({ forms, nav }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
    {forms.map((f) => (
      <span key={f.id} style={{ fontFamily: 'ui-monospace,monospace' }}>
        {f.mine
          ? <a onClick={() => nav(`/forms/${f.id}`)} style={{ color: 'var(--primary)' }}>{f.form_ref}</a>
          : f.form_ref}
        {f.status === 'CANCELLED' && <Muted> · cancelled</Muted>}
      </span>
    ))}
  </span>
);
const KV = ({ k, v, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: 13, color: 'var(--muted)' }}>{k}</span>
    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: mono ? 'ui-monospace,monospace' : 'inherit', textAlign: 'right' }}>{v}</span>
  </div>
);

/**
 * A destructive action stated as a row, not a red button.
 *
 * Two filled red buttons side by side read as a dare, and gave no clue which one
 * keeps the record. A row has space to say what actually happens, and the weight it
 * carries is proportionate: the label is red, everything else is quiet.
 */
const DangerRow = ({ label, sub, blocked, onClick, onError, last }) => (
  <AsyncButton
    type="text"
    block
    disabled={!!blocked}
    onClick={onClick}
    onError={onError}
    style={{
      height: 'auto', padding: '15px 16px', textAlign: 'left', borderRadius: 0,
      borderBottom: last ? 'none' : '1px solid var(--border)',
      // antd centres button content and forbids wrapping; both have to be undone for
      // a two-line row that reads as text rather than as a label on a button.
      justifyContent: 'flex-start', whiteSpace: 'normal',
    }}
  >
    <span style={{ display: 'block', width: '100%' }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 650, color: blocked ? 'var(--muted)' : 'var(--bad)' }}>{label}</span>
      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, whiteSpace: 'normal', marginTop: 2 }}>{sub}</span>
      {/* A blocked action stays put and says why, rather than disappearing and
          leaving the agent to guess between "not allowed" and "not yet" — the
          admin Danger Zone does the same. */}
      {blocked && (
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.45, whiteSpace: 'normal', marginTop: 5 }}>{blocked}</span>
      )}
    </span>
  </AsyncButton>
);

/**
 * The internal co-share form, as a row in the sale's document list.
 *
 * It is a document on this sale like every other one — the only difference is
 * that you generate it instead of uploading it, so it takes the same row and
 * differs only in its action. It was a card once; a card among rows, carrying a
 * full-width primary button and three lines explaining itself on every visit,
 * out-shouted the tab it was sitting in.
 *
 * One row, four states:
 *   not created → what the split is, and Generate
 *   draft       → yours to check and issue
 *   live        → whose signature is outstanding
 *   stale       → the sale's split moved after it was signed; the only one that
 *                 needs to raise its voice
 */
function CoShareFormRow({ csf, onOpen, onRaise, onError }) {
  const form = csf.form;
  const stale = !!form?.drift?.stale;

  if (!form) {
    const shares = (csf.shares || []).map((s) => `${Number(s)}%`).join(' / ');
    return (
      <div style={rowCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Internal co-share form</div>
          <AsyncButton data-tour="csf-generate" size="small" type="primary" onClick={() => onRaise(null)} onError={onError}>
            Generate
          </AsyncButton>
        </div>
        <MetaLine parts={[`${csf.agent_count} RENs`, shares || null, 'not created yet']} />
      </div>
    );
  }

  return (
    <div
      onClick={() => onOpen(form.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(form.id); } }}
      style={{ ...rowCard, cursor: 'pointer', ...(stale ? { borderColor: 'color-mix(in srgb,var(--bad) 40%,var(--border))' } : null) }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Internal co-share form</div>
        <Chip label={CS_LABEL[form.status] || form.status} tone={CS_TONE[form.status]} />
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 4, fontFamily: 'ui-monospace,monospace', letterSpacing: '-.01em' }}>
        {form.form_ref}{form.version > 1 ? ` · v${form.version}` : ''}
      </div>
      <MetaLine parts={[
        form.status === 'DRAFT' ? 'not issued yet' : `${form.signed_count} of ${form.signatory_count} signed`,
        `${csf.agent_count} RENs`,
      ]} />
      {/* Only the states that need something from someone say anything more. */}
      {stale ? (
        <div style={{ fontSize: 11.5, color: 'var(--bad)', fontWeight: 600, marginTop: 6 }}>
          No longer matches the sale — reissue it.
        </div>
      ) : form.my_signature_pending ? (
        <div style={{ fontSize: 11.5, color: 'var(--warn)', fontWeight: 600, marginTop: 6 }}>
          Waiting on your signature.
        </div>
      ) : null}
    </div>
  );
}
